import React, { useState, useEffect, useRef } from 'react';
import { 
  LayoutDashboard, FilePlus2, PackageSearch, Users, 
  Settings, Ship, Search, UserCircle, 
  MapPin, Calendar, Car, ArrowRight, CheckCircle2,
  X, Download, Edit, Save, ChevronLeft, ChevronRight,
  Printer, Lock, LogOut, Eye, EyeOff, Trash2, AlertCircle, Route, Plus, Database, Sparkles,
  Contact, User, Home, ClipboardCheck,
  UploadCloud, Image as ImageIcon, Loader2
} from 'lucide-react';

// ==================== FUNGSI PEMBANTU ====================
function terbilang(angka) {
  const bilangan = [
    '', 'Satu', 'Dua', 'Tiga', 'Empat', 'Lima', 
    'Enam', 'Tujuh', 'Delapan', 'Sembilan', 'Sepuluh', 'Sebelas'
  ];
  
  let temp = '';
  const n = Math.floor(angka);
  
  if (n < 12) {
    temp = ' ' + bilangan[n];
  } else if (n < 20) {
    temp = terbilang(n - 10) + ' Belas';
  } else if (n < 100) {
    temp = terbilang(n / 10) + ' Puluh' + terbilang(n % 10);
  } else if (n < 200) {
    temp = ' Seratus' + terbilang(n - 100);
  } else if (n < 1000) {
    temp = terbilang(n / 100) + ' Ratus' + terbilang(n % 100);
  } else if (n < 2000) {
    temp = ' Seribu' + terbilang(n - 1000);
  } else if (n < 1000000) {
    temp = terbilang(n / 1000) + ' Ribu' + terbilang(n % 1000);
  } else if (n < 1000000000) {
    temp = terbilang(n / 1000000) + ' Juta ' + terbilang(n % 1000000);
  }
  
  return temp.replace(/\s+/g, ' ').trim();
}

const formatLabel = (str) => {
  if (!str) return '-';
  
  // 1. Hapus akhiran ' dtd' atau '_dtd' secara case-insensitive
  let cleanedStr = str.replace(/(?:\s|_)?dtd$/i, '').trim();
  
  // 2. Penyesuaian khusus untuk truk besar
  let lowerCleaned = cleanedStr.toLowerCase();
  if (lowerCleaned === 'truk besar' || lowerCleaned === 'truk_besar_tronton' || lowerCleaned === 'truk_besar') {
    return 'Truk Besar/Tronton';
  }

  // 3. Kembalikan format dengan huruf kapital di awal setiap kata
  return cleanedStr.split(/[_ ]+/).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

// ==================== STORAGE SERVICE & UPLOADER ====================
const compressImage = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        const maxSize = 1200;
        
        if (width > height && width > maxSize) {
          height *= maxSize / width;
          width = maxSize;
        } else if (height > maxSize) {
          width *= maxSize / height;
          height = maxSize;
        }
        
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob); // Resolve as Blob to avoid browser-specific File constructor issues
          } else {
            reject(new Error("Canvas to Blob failed"));
          }
        }, 'image/webp', 0.8);
      };
      img.onerror = () => reject(new Error("Gagal memuat gambar"));
    };
    reader.onerror = (error) => reject(error);
  });
};

const TrackingPhotoUploader = ({ files, setFiles }) => {
  const [errorMsg, setErrorMsg] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    setErrorMsg('');
    const selected = Array.from(e.target.files);
    
    if (files.length + selected.length > 5) {
      setErrorMsg('Maksimal hanya dapat mengunggah 5 foto.');
      return;
    }

    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const newFiles = [];

    for (let file of selected) {
      if (!validTypes.includes(file.type)) {
        setErrorMsg(`Format ${file.name} tidak didukung. Gunakan JPG, PNG, atau WEBP.`);
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        setErrorMsg(`${file.name} melebihi 5MB sebelum kompresi.`);
        continue;
      }
      
      try {
        const blob = await compressImage(file);
        newFiles.push({
          file: blob,
          preview: URL.createObjectURL(blob),
          id: Math.random().toString(36).substring(7)
        });
      } catch (err) {
        setErrorMsg(`Gagal memproses gambar ${file.name}`);
      }
    }
    
    setFiles(prev => [...prev, ...newFiles]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (idToRemove) => {
    setFiles(files.filter(f => f.id !== idToRemove));
    setErrorMsg('');
  };

  return (
    <div className="space-y-3">
      {errorMsg && (
        <div className="bg-red-50 text-red-600 text-xs px-3 py-2 rounded-lg font-medium flex items-center gap-2">
          <AlertCircle size={14} className="shrink-0" /> {errorMsg}
        </div>
      )}
      
      {files.length < 5 && (
        <div 
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-slate-300 rounded-xl p-6 flex flex-col items-center justify-center text-slate-500 hover:bg-slate-50 hover:border-blue-400 hover:text-blue-500 transition-all cursor-pointer bg-slate-50/50"
        >
          <UploadCloud size={32} className="mb-2 opacity-70" />
          <p className="text-sm font-semibold mb-1">Drag & Drop atau Pilih Foto</p>
          <p className="text-[10px]">JPG, PNG, WEBP (Max 5MB) - Maks 5 Foto</p>
          <input 
            type="file" 
            ref={fileInputRef} 
            onChange={handleFileSelect} 
            accept="image/jpeg, image/png, image/webp" 
            multiple 
            className="hidden" 
          />
        </div>
      )}

      {files.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
          {files.map((item) => (
            <div key={item.id} className="relative group rounded-lg overflow-hidden border border-slate-200 aspect-square bg-slate-100 flex items-center justify-center">
              <img src={item.preview} alt="Preview" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <button type="button" onClick={(e) => { e.stopPropagation(); removeFile(item.id); }} className="text-white hover:text-red-400 p-2 transform hover:scale-110 transition-all cursor-pointer">
                  <Trash2 size={20} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== KONEKSI DATABASE PRODUCTION ====================
const PRODUCTION_SUPABASE_URL = "https://ysacdovoviqahjlwkwqh.supabase.co";       
const PRODUCTION_SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InlzYWNkb3ZvdmlxYWhqbHdrd3FoIiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODE4OTI0NTAsImV4cCI6MjA5NzQ2ODQ1MH0.kUirbG58-e9uxuq1YEdi1yZ4WhYbBzcJovKlrjZp2QI";  

// ==================== INITIAL LOCAL FALLBACK DATA ====================
const INITIAL_CUSTOMERS = [
  { id_customer: 'b0eebc99-9c0b-4ef8-bb6d-6bb9bd380a11', nama_pelanggan: 'PT. Astra Auto', no_ktp: '3173010101900002', no_wa: '081234567890', total_kiriman: 5 },
];

const INITIAL_PRICE_RATES = [
  { id: 1, origin: 'Jakarta', destination: 'Surabaya', golongan: 'Golongan I (Motor < 250cc)', harga_dasar: 500000 },
  { id: 2, origin: 'Jakarta', destination: 'Surabaya', golongan: 'Golongan II (Motor > 250cc)', harga_dasar: 750000 },
  { id: 3, origin: 'Jakarta', destination: 'Surabaya', golongan: 'Golongan III (Mobil <= 5m)', harga_dasar: 1250000 },
];

const INITIAL_PRICE_TAMBAHAN = [];
const INITIAL_PRICE_DTD = [];
const INITIAL_ORDERS = [];

const CHECKLIST_ITEMS = [
  "STNK Asli", "Kunci Kontak", "Kunci Serep", "Kaca Spion Luar", "Kaca Spion Dalam",
  "Dop Roda", "Ban Serep", "Tape/Radio/CD", "LCD TV", "Remote Tape",
  "Emblem Logo", "Jam Terpasang", "Buku Service/Manual", "Cat Cadangan", "Karpet Depan",
  "Karpet Belakang", "Dongkrak/Stang", "Sock Set/Tool Set", "Kunci Roda", "Kotak P3K",
  "Segitiga Pengaman", "Pemantik Api", "Talang Air Pintu Depan", "Talang Air Pintu Spg Blkg",
  "Penahan Sinar Matahari", "Antena"
];

// ==================== KOMPONEN LOGIN SCREEN ====================
const LoginScreen = ({ onLogin, externalError, onOpenConfig, isConnected }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    setIsLoading(true);
    await onLogin(email, password);
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8 font-sans select-none animate-in fade-in duration-300">
      <div className="sm:mx-auto sm:w-full sm:max-w-md px-4">
        <div className="flex justify-center mb-6">
          <img src="/logo-antaroto.png" alt="AntarOto Logo" className="h-16 md:h-20 w-auto drop-shadow-md" />
        </div>
        <h2 className="text-center text-3xl font-black text-slate-900 tracking-tight">AntarOto</h2>
        <p className="mt-1.5 text-center text-sm text-slate-500 font-medium">Portal Administrasi & Operasional Sistem</p>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md bg-white p-6 sm:p-8 rounded-2xl border border-slate-200/80 shadow-lg">
          <form className="space-y-5" onSubmit={handleLogin}>
            {externalError && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-xs font-semibold flex items-center gap-2 animate-in slide-in-from-top-1">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0"></span>{externalError}
              </div>
            )}
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Email</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none"><UserCircle size={18} className="text-slate-400" /></div>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="appearance-none block w-full pl-11 pr-4 py-3.5 border border-slate-200 rounded-xl bg-slate-50/50 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-sm font-medium text-slate-800" placeholder="Masukkan email terdaftar..." />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Kata Sandi</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none"><Lock size={18} className="text-slate-400" /></div>
                <input type={showPassword ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} className="appearance-none block w-full pl-11 pr-12 py-3.5 border border-slate-200 rounded-xl bg-slate-50/50 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-sm font-medium text-slate-800" placeholder="••••••••" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 transition-colors">
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div className="pt-2">
              <button type="submit" disabled={isLoading} className="w-full flex justify-center py-3.5 px-4 border border-transparent rounded-xl shadow-md shadow-blue-100 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all disabled:opacity-60 active:scale-[0.99]">
                {isLoading ? 'Memverifikasi...' : 'Masuk ke Dashboard'}
              </button>
            </div>
          </form>
          {!isConnected && (
            <div className="mt-6 border-t border-slate-100 pt-4 text-center">
              <p className="text-xs text-slate-400">Uji coba tanpa setup database? Gunakan email <strong className="text-slate-600">admin@antaroto.com</strong> & sandi <strong className="text-slate-600">admin123</strong> untuk offline mode.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ===============================================================
const App = () => {
  const [supabaseLoaded, setSupabaseLoaded] = useState(false);
  const [supabaseUrl, setSupabaseUrl] = useState(PRODUCTION_SUPABASE_URL || localStorage.getItem('antaroto_supabase_url') || '');
  const [supabaseKey, setSupabaseKey] = useState(PRODUCTION_SUPABASE_ANON_KEY || localStorage.getItem('antaroto_supabase_key') || '');
  const [supabaseClient, setSupabaseClient] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);

  const [session, setSession] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [loginError, setLoginError] = useState('');
  
  const [orders, setOrders] = useState(INITIAL_ORDERS);
  const [customers, setCustomers] = useState(INITIAL_CUSTOMERS);
  const [golonganList, setGolonganList] = useState(INITIAL_PRICE_RATES); 
  const [layananTambahanList, setLayananTambahanList] = useState(INITIAL_PRICE_TAMBAHAN);
  const [layananDtdList, setLayananDtdList] = useState(INITIAL_PRICE_DTD);
  const [isFetching, setIsFetching] = useState(false);

  const [submitError, setSubmitError] = useState('');
  const [editError, setEditError] = useState('');

  const [isSelectCustomerModalOpen, setIsSelectCustomerModalOpen] = useState(false);
  const [searchCustomerQuery, setSearchCustomerQuery] = useState('');

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const existingScript = document.getElementById('supabase-js-cdn');
      if (!existingScript) {
        const script = document.createElement('script');
        script.id = 'supabase-js-cdn';
        script.src = "https://cdn.jsdelivr.net/npm/@supabase/supabase-js@2";
        script.async = true;
        script.onload = () => setSupabaseLoaded(true);
        script.onerror = () => setSupabaseLoaded(false);
        document.body.appendChild(script);
      } else {
        setSupabaseLoaded(true);
      }
    }
  }, []);

  useEffect(() => {
    if (supabaseLoaded && supabaseUrl && supabaseKey && window.supabase) {
      try {
        const client = window.supabase.createClient(supabaseUrl, supabaseKey);
        setSupabaseClient(client);

        client.auth.getSession().then(({ data: { session: activeSession } }) => {
          if (activeSession) setSession(activeSession);
          setLoadingAuth(false);
        }).catch(() => setLoadingAuth(false));

        const { data: { subscription } } = client.auth.onAuthStateChange((_event, activeSession) => {
          setSession(activeSession);
        });

        return () => { if (subscription) subscription.unsubscribe(); };
      } catch (err) {
        setSupabaseClient(null);
        setLoadingAuth(false);
      }
    } else {
      setSupabaseClient(null);
      const localOfflineUser = localStorage.getItem('antaroto_offline_user');
      if (localOfflineUser) setSession({ user: { email: localOfflineUser } });
      setLoadingAuth(false);
    }
  }, [supabaseLoaded, supabaseUrl, supabaseKey]);

  useEffect(() => {
    if (session) tarikDataDatabase();
  }, [session, supabaseClient]);

  async function tarikDataDatabase() {
    if (!supabaseClient) return;
    setIsFetching(true);
    try {
      const { data: ordersData, error: ordersErr } = await supabaseClient.from('orders').select('*').order('created_at', { ascending: false });
      if (!ordersErr && ordersData) setOrders(ordersData);

      const { data: customersData, error: custsErr } = await supabaseClient.from('customers').select('*').order('created_at', { ascending: false });
      if (!custsErr && customersData) setCustomers(customersData);

      const { data: ratesData, error: ratesErr } = await supabaseClient.from('price_rates').select('*').order('id', { ascending: true });
      if (!ratesErr && ratesData) setGolonganList(ratesData); 

      const { data: tambahanData, error: tambahanErr } = await supabaseClient.from('price_asuransi').select('*').order('id_price_asuransi', { ascending: true });
      if (!tambahanErr && tambahanData) setLayananTambahanList(tambahanData);

      const { data: dtdData, error: dtdErr } = await supabaseClient.from('price_dtd').select('*').order('id_price_dtd', { ascending: true });
      if (!dtdErr && dtdData) setLayananDtdList(dtdData);

    } catch (err) {
      console.warn("Gagal sinkronisasi data dengan Supabase server:", err);
    } finally {
      setIsFetching(false);
    }
  }

  const handleSaveConfig = (e, url, key) => {
    e.preventDefault();
    localStorage.setItem('antaroto_supabase_url', url);
    localStorage.setItem('antaroto_supabase_key', key);
    setSupabaseUrl(url);
    setSupabaseKey(key);
    setShowConfigModal(false);
  };

  const handleClearConfig = () => {
    localStorage.removeItem('antaroto_supabase_url');
    localStorage.removeItem('antaroto_supabase_key');
    setSupabaseUrl('');
    setSupabaseKey('');
    setSupabaseClient(null);
    setShowConfigModal(false);
  };

  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [generatedResi, setGeneratedResi] = useState('');
  const [searchQuery, setSearchQuery] = useState(''); 
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const mainContentRef = useRef(null);

  const handleLoginAttempt = async (email, password) => {
    setLoginError('');
    if (supabaseClient) {
      try {
        const { data, error } = await supabaseClient.auth.signInWithPassword({ email, password });
        if (error) setLoginError('Kombinasi email/password salah: ' + error.message);
        else if (data && data.session) setSession(data.session);
      } catch (err) {
        setLoginError('Koneksi internet bermasalah saat menghubungi server.');
      }
    } else {
      if (email === 'admin@antaroto.com' && password === 'admin123') {
        localStorage.setItem('antaroto_offline_user', email);
        setSession({ user: { email: email } });
      } else {
        setLoginError('Koneksi database offline. Gunakan email "admin@antaroto.com" & sandi "admin123".');
      }
    }
  };

  const handleLogout = async () => {
    if (supabaseClient) await supabaseClient.auth.signOut();
    localStorage.removeItem('antaroto_offline_user');
    setSession(null);
    setActiveMenu('dashboard');
    setSearchQuery('');
  };

  // ==================== AUTO LOGOUT (15 MENIT) ====================
  useEffect(() => {
    let timeoutId;
    
    const performAutoLogout = async () => {
      await handleLogout();
      setLoginError('Sesi berakhir secara otomatis karena tidak ada aktivitas selama 15 menit.');
    };

    const resetTimer = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(performAutoLogout, 15 * 60 * 1000); // 15 Menit = 900.000 ms
    };

    if (session) {
      resetTimer(); // Mulai timer saat login
      const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
      
      const handleActivity = () => {
        resetTimer(); // Reset timer saat ada aktivitas
      };

      activityEvents.forEach(event => {
        window.addEventListener(event, handleActivity, { passive: true });
      });

      return () => {
        if (timeoutId) clearTimeout(timeoutId);
        activityEvents.forEach(event => {
          window.removeEventListener(event, handleActivity, { passive: true });
        });
      };
    }
  }, [session, supabaseClient]);
  // ================================================================

  const currentUser = session && session.user ? {
    name: session.user.email ? session.user.email.split('@')[0] : 'Staff',
    email: session.user.email,
    role: session.user.email === 'admin@antaroto.com' ? 'Super Admin' : 'Staff'
  } : null;

  const changeMenu = (menu) => {
    setActiveMenu(menu);
    setSearchQuery(''); 
    setSubmitError('');
    if (mainContentRef.current) mainContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const [isAddCustomerModalOpen, setIsAddCustomerModalOpen] = useState(false);
  const [customerFormData, setCustomerFormData] = useState({ nama_pelanggan: '', no_wa: '', no_ktp: '' });
  const [customerFormError, setCustomerFormError] = useState('');

  const [isEditCustomerModalOpen, setIsEditCustomerModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [isDeleteCustomerModalOpen, setIsDeleteCustomerModalOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  
  const [trackingPhotos, setTrackingPhotos] = useState([]);
  const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);

  const [masterBiayaTab, setMasterBiayaTab] = useState('rates');

  const [isTarifModalOpen, setIsTarifModalOpen] = useState(false);
  const [tarifFormData, setTarifFormData] = useState({ id: '', origin: '', destination: '', golongan: '', harga_dasar: '' });
  const [tarifFormError, setTarifFormError] = useState('');
  const [isDeleteTarifModalOpen, setIsDeleteTarifModalOpen] = useState(false);
  const [tarifToDelete, setTarifToDelete] = useState(null);

  const [isTambahanModalOpen, setIsTambahanModalOpen] = useState(false);
  const [tambahanFormData, setTambahanFormData] = useState({ id_price_asuransi: '', nama_layanan: '', tipe_kendaraan: '', harga_asuransi: '' });
  const [tambahanFormError, setTambahanFormError] = useState('');
  const [isDeleteTambahanModalOpen, setIsDeleteTambahanModalOpen] = useState(false);
  const [tambahanToDelete, setTambahanToDelete] = useState(null);

  const [isDtdModalOpen, setIsDtdModalOpen] = useState(false);
  const [dtdFormData, setDtdFormData] = useState({ id_price_dtd: '', nama_layanan: 'door_to_door', tipe_kendaraan: 'mobil', harga_dtd: '' });
  const [dtdFormError, setDtdFormError] = useState('');
  const [isDeleteDtdModalOpen, setIsDeleteDtdModalOpen] = useState(false);
  const [dtdToDelete, setDtdToDelete] = useState(null);

  // State untuk Modal Cek Fisik Kendaraan
  const [isChecklistModalOpen, setIsChecklistModalOpen] = useState(false);
  const [selectedChecklistOrder, setSelectedChecklistOrder] = useState(null);
  const [checklistFormData, setChecklistFormData] = useState({
    no_rangka: '', no_mesin: '', no_surat_jalan: '', km_speedometer: '',
    items: {}, catatan_body: '', parts_damaged: []
  });

  const [formData, setFormData] = useState({
    nama_pengirim: '', no_ktp_pengirim: '', no_wa_pengirim: '', alamat_pengirim: '',
    nama_penerima: '', no_wa_penerima: '', alamat_penerima: '', 
    merk_tipe_kendaraan: '', warna_kendaraan: '', nomor_polisi: '', tahun_kendaraan: '', golongan_kendaraan: '', catatan_kendaraan: '', 
    origin: '', destination: '', harga_dasar_snapshot: '', tanggal_muat: '', 
    asuransi_id: '', door_to_door_id: '',
    catatan_pengiriman: ''
  });

  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleNumberChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value.replace(/[^0-9]/g, '') });

  const handleTarifInputChange = (e) => setTarifFormData({ ...tarifFormData, [e.target.name]: e.target.value });
  const handleTarifPriceChange = (e) => setTarifFormData({ ...tarifFormData, harga_dasar: e.target.value.replace(/[^0-9]/g, '') });

  const handleTambahanInputChange = (e) => setTambahanFormData({ ...tambahanFormData, [e.target.name]: e.target.value });
  const handleTambahanPriceChange = (e) => setTambahanFormData({ ...tambahanFormData, harga_asuransi: e.target.value.replace(/[^0-9]/g, '') });

  const handleDtdInputChange = (e) => setDtdFormData({ ...dtdFormData, [e.target.name]: e.target.value });
  const handleDtdPriceChange = (e) => setDtdFormData({ ...dtdFormData, harga_dtd: e.target.value.replace(/[^0-9]/g, '') });

  const saveTarif = async (e) => {
    e.preventDefault();
    setTarifFormError('');
    const finalTarif = {
      origin: tarifFormData.origin,
      destination: tarifFormData.destination,
      golongan: tarifFormData.golongan,
      harga_dasar: Number(tarifFormData.harga_dasar)
    };

    if (supabaseClient) {
      setIsSubmitting(true);
      try {
        if (tarifFormData.id) {
          const { error } = await supabaseClient
            .from('price_rates')
            .update(finalTarif)
            .eq('id', tarifFormData.id);
          if (error) throw error;
        } else {
          const { error } = await supabaseClient
            .from('price_rates')
            .insert([finalTarif]);
          if (error) throw error;
        }
        await tarikDataDatabase();
        setIsTarifModalOpen(false);
      } catch (err) {
        setTarifFormError("Gagal menyimpan ke Supabase: " + err.message);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      let updatedList;
      if (tarifFormData.id) {
        const updatedTarifObj = { ...finalTarif, id: tarifFormData.id };
        updatedList = golonganList.map(r => r.id === tarifFormData.id ? updatedTarifObj : r);
      } else {
        const nextIdNum = Math.max(0, ...golonganList.map(g => Number(g.id) || 0)) + 1;
        const newTarifObj = { ...finalTarif, id: nextIdNum };
        updatedList = [newTarifObj, ...golonganList];
      }
      setGolonganList(updatedList);
      setIsTarifModalOpen(false);
    }
  };

  const openAddTarifModal = () => {
    setTarifFormData({ id: '', origin: '', destination: '', golongan: '', harga_dasar: '' });
    setTarifFormError('');
    setIsTarifModalOpen(true);
  };
  const openEditTarifModal = (tarif) => {
    setTarifFormData({ ...tarif });
    setTarifFormError('');
    setIsTarifModalOpen(true);
  };
  const openDeleteTarifModal = (tarif) => {
    setTarifToDelete(tarif);
    setIsDeleteTarifModalOpen(true);
  };
  const confirmDeleteTarif = async () => {
    if (tarifToDelete) {
      if (supabaseClient) {
        try {
          const { error } = await supabaseClient
            .from('price_rates')
            .delete()
            .eq('id', tarifToDelete.id);
          if (error) throw error;
          await tarikDataDatabase();
        } catch (err) {
          setSubmitError("Gagal menghapus tarif dari Supabase: " + err.message);
        }
      } else {
        const updatedList = golonganList.filter(r => r.id !== tarifToDelete.id);
        setGolonganList(updatedList);
      }
      setIsDeleteTarifModalOpen(false);
      setTarifToDelete(null);
    }
  };

  const saveTambahan = async (e) => {
    e.preventDefault();
    setTambahanFormError('');
    const finalTambahan = {
      nama_layanan: tambahanFormData.nama_layanan,
      tipe_kendaraan: tambahanFormData.tipe_kendaraan,
      harga_asuransi: Number(tambahanFormData.harga_asuransi)
    };

    if (supabaseClient) {
      setIsSubmitting(true);
      try {
        if (tambahanFormData.id_price_asuransi) {
          const { error } = await supabaseClient
            .from('price_asuransi')
            .update(finalTambahan)
            .eq('id_price_asuransi', tambahanFormData.id_price_asuransi);
          if (error) throw error;
        } else {
          const { error } = await supabaseClient
            .from('price_asuransi')
            .insert([finalTambahan]);
          if (error) throw error;
        }
        await tarikDataDatabase();
        setIsTambahanModalOpen(false);
      } catch (err) {
        setTambahanFormError("Gagal menyimpan ke Supabase: " + err.message);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      let updatedList;
      if (tambahanFormData.id_price_asuransi) {
        const updatedTambahanObj = { ...finalTambahan, id_price_asuransi: tambahanFormData.id_price_asuransi };
        updatedList = layananTambahanList.map(t => t.id_price_asuransi === tambahanFormData.id_price_asuransi ? updatedTambahanObj : t);
      } else {
        const nextIdNum = Math.max(0, ...layananTambahanList.map(t => Number(t.id_price_asuransi) || 0)) + 1;
        const newTambahanObj = { ...finalTambahan, id_price_asuransi: nextIdNum };
        updatedList = [newTambahanObj, ...layananTambahanList];
      }
      setLayananTambahanList(updatedList);
      setIsTambahanModalOpen(false);
    }
  };

  const openAddTambahanModal = () => {
    setTambahanFormData({ id_price_asuransi: '', nama_layanan: 'asuransi', tipe_kendaraan: 'mobil', harga_asuransi: '' });
    setTambahanFormError('');
    setIsTambahanModalOpen(true);
  };
  const openEditTambahanModal = (tambahan) => {
    setTambahanFormData({ ...tambahan });
    setTambahanFormError('');
    setIsTambahanModalOpen(true);
  };
  const openDeleteTambahanModal = (tambahan) => {
    setTambahanToDelete(tambahan);
    setIsDeleteTambahanModalOpen(true);
  };
  const confirmDeleteTambahan = async () => {
    if (tambahanToDelete) {
      if (supabaseClient) {
        try {
          const { error } = await supabaseClient
            .from('price_asuransi')
            .delete()
            .eq('id_price_asuransi', tambahanToDelete.id_price_asuransi);
          if (error) throw error;
          await tarikDataDatabase();
        } catch (err) {
          setSubmitError("Gagal menghapus data asuransi dari Supabase: " + err.message);
        }
      } else {
        const updatedList = layananTambahanList.filter(t => t.id_price_asuransi !== tambahanToDelete.id_price_asuransi);
        setLayananTambahanList(updatedList);
      }
      setIsDeleteTambahanModalOpen(false);
      setTambahanToDelete(null);
    }
  };

  const saveDtd = async (e) => {
    e.preventDefault();
    setDtdFormError('');
    const finalDtd = {
      nama_layanan: dtdFormData.nama_layanan,
      tipe_kendaraan: dtdFormData.tipe_kendaraan,
      harga_dtd: Number(dtdFormData.harga_dtd)
    };

    if (supabaseClient) {
      setIsSubmitting(true);
      try {
        if (dtdFormData.id_price_dtd) {
          const { error } = await supabaseClient
            .from('price_dtd')
            .update(finalDtd)
            .eq('id_price_dtd', dtdFormData.id_price_dtd);
          if (error) throw error;
        } else {
          const { error } = await supabaseClient
            .from('price_dtd')
            .insert([finalDtd]);
          if (error) throw error;
        }
        await tarikDataDatabase();
        setIsDtdModalOpen(false);
      } catch (err) {
        setDtdFormError("Gagal menyimpan ke Supabase: " + err.message);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      let updatedList;
      if (dtdFormData.id_price_dtd) {
        const updatedDtdObj = { ...finalDtd, id_price_dtd: dtdFormData.id_price_dtd };
        updatedList = layananDtdList.map(t => t.id_price_dtd === dtdFormData.id_price_dtd ? updatedDtdObj : t);
      } else {
        const nextIdNum = Math.max(0, ...layananDtdList.map(t => Number(t.id_price_dtd) || 0)) + 1;
        const newDtdObj = { ...finalDtd, id_price_dtd: nextIdNum };
        updatedList = [newDtdObj, ...layananDtdList];
      }
      setLayananDtdList(updatedList);
      setIsDtdModalOpen(false);
    }
  };

  const openAddDtdModal = () => {
    setDtdFormData({ id_price_dtd: '', nama_layanan: 'door_to_door', tipe_kendaraan: 'mobil', harga_dtd: '' });
    setDtdFormError('');
    setIsDtdModalOpen(true);
  };
  const openEditDtdModal = (dtd) => {
    setDtdFormData({ ...dtd });
    setDtdFormError('');
    setIsDtdModalOpen(true);
  };
  const openDeleteDtdModal = (dtd) => {
    setDtdToDelete(dtd);
    setIsDeleteDtdModalOpen(true);
  };
  const confirmDeleteDtd = async () => {
    if (dtdToDelete) {
      if (supabaseClient) {
        try {
          const { error } = await supabaseClient
            .from('price_dtd')
            .delete()
            .eq('id_price_dtd', dtdToDelete.id_price_dtd);
          if (error) throw error;
          await tarikDataDatabase();
        } catch (err) {
          setSubmitError("Gagal menghapus data Door to Door dari Supabase: " + err.message);
        }
      } else {
        const updatedList = layananDtdList.filter(t => t.id_price_dtd !== dtdToDelete.id_price_dtd);
        setLayananDtdList(updatedList);
      }
      setIsDeleteDtdModalOpen(false);
      setDtdToDelete(null);
    }
  };

  const openAddCustomerModal = () => {
    setCustomerFormData({ nama_pelanggan: '', no_wa: '', no_ktp: '' });
    setCustomerFormError('');
    setIsAddCustomerModalOpen(true);
  };

  const saveAddCustomer = async (e) => {
    e.preventDefault();
    setCustomerFormError('');
    
    const newCust = {
      nama_pelanggan: customerFormData.nama_pelanggan,
      no_wa: customerFormData.no_wa || '-',
      no_ktp: customerFormData.no_ktp || '-',
      total_kiriman: 0
    };

    if (supabaseClient) {
      setIsSubmitting(true);
      try {
        const { error } = await supabaseClient
          .from('customers')
          .upsert(newCust, { onConflict: 'no_wa' });
        if (error) throw error;
        await tarikDataDatabase();
        setIsAddCustomerModalOpen(false);
      } catch (err) {
        setCustomerFormError("Gagal menyimpan ke Supabase: " + err.message);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      const nextIdNum = customers.length + 1;
      const newCustLocal = {
        id_customer: `CUST-LOCAL-${nextIdNum}`,
        ...newCust
      };
      setCustomers([newCustLocal, ...customers]);
      setIsAddCustomerModalOpen(false);
    }
  };

  const openEditCustomerModal = (customer) => {
    setEditingCustomer({ ...customer });
    setCustomerFormError('');
    setIsEditCustomerModalOpen(true);
  };
  const handleEditCustomerChange = (e) => {
    const { name, value } = e.target;
    setEditingCustomer({ 
      ...editingCustomer, 
      [name]: (name === 'no_wa' || name === 'no_ktp') ? value.replace(/[^0-9]/g, '') : value 
    });
  };
  
  const saveEditCustomer = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setCustomerFormError('');
    
    const payload = {
      nama_pelanggan: editingCustomer.nama_pelanggan,
      no_wa: editingCustomer.no_wa,
      no_ktp: editingCustomer.no_ktp
    };

    if (supabaseClient) {
      const { error } = await supabaseClient
        .from('customers')
        .update(payload)
        .eq('id_customer', editingCustomer.id_customer);
        
      if (error) {
        setCustomerFormError("Gagal update data pelanggan: " + error.message);
        setIsSubmitting(false);
        return;
      }
    }

    const originalCustomer = customers.find(c => c.id_customer === editingCustomer.id_customer);
    const updatedCustomers = customers.map(c => c.id_customer === editingCustomer.id_customer ? { ...c, ...payload } : c);
    setCustomers(updatedCustomers);

    if (originalCustomer) {
      const updatedOrders = orders.map(order => {
        if (order.id_customer === originalCustomer.id_customer || order.nama_pengirim === originalCustomer.nama_pelanggan || order.no_wa_pengirim === originalCustomer.no_wa) {
          const syncOrder = { 
            ...order, 
            nama_pengirim: editingCustomer.nama_pelanggan, 
            no_wa_pengirim: editingCustomer.no_wa, 
            no_ktp_pengirim: editingCustomer.no_ktp 
          };
          
          if (supabaseClient) {
            supabaseClient.from('orders').update({
              nama_pengirim: editingCustomer.nama_pelanggan, 
              no_wa_pengirim: editingCustomer.no_wa, 
              no_ktp_pengirim: editingCustomer.no_ktp 
            }).eq('id_order', order.id_order).then(({error}) => {
              if (error) console.warn("Sync orders error:", error.message);
            });
          }
          return syncOrder;
        }
        return order;
      });
      setOrders(updatedOrders);
    }
    
    if (supabaseClient) {
      await tarikDataDatabase();
    }

    setIsSubmitting(false);
    setIsEditCustomerModalOpen(false);
    setEditingCustomer(null);
  };
  
  const openDeleteCustomerModal = (customer) => {
    setCustomerToDelete(customer);
    setIsDeleteCustomerModalOpen(true);
  };
  const confirmDeleteCustomer = async () => {
    if (customerToDelete) {
      setCustomers(customers.filter(c => c.id_customer !== customerToDelete.id_customer));
      if (supabaseClient) {
        await supabaseClient.from('customers').delete().eq('id_customer', customerToDelete.id_customer);
      }
      setIsDeleteCustomerModalOpen(false);
      setCustomerToDelete(null);
    }
  };

  const pilihPelangganLama = (customer) => {
    setFormData(prev => ({
      ...prev,
      nama_pengirim: customer.nama_pelanggan,
      no_wa_pengirim: customer.no_wa,
      no_ktp_pengirim: customer.no_ktp
    }));
    setIsSelectCustomerModalOpen(false);
    setSearchCustomerQuery('');
  };

  const basePorts = ["Jakarta", "Surabaya", "Makassar", "Balikpapan"];
  const ports = Array.from(new Set([
    ...basePorts,
    ...golonganList.map(g => g.origin),
    ...golonganList.map(g => g.destination)
  ])).filter(Boolean).sort();

  const uniqueGolonganOptions = Array.from(
    new Set(golonganList.map(item => item.golongan))
  ).filter(Boolean).sort();

  useEffect(() => {
    if (formData.origin && formData.destination && formData.golongan_kendaraan) {
      if (formData.origin === formData.destination) {
        setFormData(prev => ({ ...prev, harga_dasar_snapshot: 0 }));
      } else {
        let calculatedPrice = 0;
        const exactRate = golonganList.find(r => 
          r.origin === formData.origin && r.destination === formData.destination && r.golongan === formData.golongan_kendaraan
        );

        if (exactRate && exactRate.harga_dasar) {
          calculatedPrice = Number(exactRate.harga_dasar);
        } else {
          const basePrice = 3500000;
          const stringVar = formData.origin.charCodeAt(0) + formData.destination.charCodeAt(0);
          const lengthVar = Math.abs(formData.origin.length - formData.destination.length) * 100000;
          calculatedPrice = basePrice + (stringVar * 10000) + lengthVar;
          calculatedPrice = Math.round(calculatedPrice / 50000) * 50000; 
        }
        
        const selectedAsuransi = layananTambahanList.find(t => t.id_price_asuransi?.toString() === formData.asuransi_id);
        if (selectedAsuransi) calculatedPrice += Number(selectedAsuransi.harga_asuransi);

        const selectedDTD = layananDtdList.find(t => t.id_price_dtd?.toString() === formData.door_to_door_id);
        if (selectedDTD) calculatedPrice += Number(selectedDTD.harga_dtd);

        setFormData(prev => ({ ...prev, harga_dasar_snapshot: calculatedPrice }));
      }
    } else {
      setFormData(prev => ({ ...prev, harga_dasar_snapshot: '' }));
    }
  }, [formData.origin, formData.destination, formData.asuransi_id, formData.door_to_door_id, formData.golongan_kendaraan, golonganList, layananTambahanList, layananDtdList]);

  const totalPendapatan = orders
    .filter(o => o.status_payment === 'Lunas')
    .reduce((sum, order) => sum + (Number(order.harga_dasar_snapshot) || 0), 0);
  const countBelum = orders.filter(o => o.status_payment === 'Belum').length;
  const countLunas = orders.filter(o => o.status_payment === 'Lunas').length;

  const stats = [
    { type: 'standard', label: 'Pendapatan Diterima (Lunas)', value: `Rp ${totalPendapatan.toLocaleString('id-ID')}`, increase: 'Akumulasi' },
    { type: 'payment', label: 'Status Pembayaran', lunas: countLunas, belum: countBelum },
    { type: 'standard', label: 'Total Pelanggan', value: customers.length.toString(), increase: 'Aktif' },
  ];

  const handleEntrySubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    const randomId = Math.floor(1000 + Math.random() * 9000);
    const newResi = `AO-2606-${randomId}`;

    let finalCustomerId = '';
    
    if (formData.nama_pengirim) {
      if (supabaseClient) {
        const { data: customerData, error: custErr } = await supabaseClient
          .from('customers')
          .upsert({
            nama_pelanggan: formData.nama_pengirim,
            no_ktp: formData.no_ktp_pengirim || '-',
            no_wa: formData.no_wa_pengirim || '-'
          }, { onConflict: 'no_wa' })
          .select();

        if (custErr) {
          setSubmitError(`Gagal simpan/update data pelanggan: ${custErr.message}. (Pastikan Constraint Unique pada 'no_wa' aktif)`);
          setIsSubmitting(false);
          return;
        }
        finalCustomerId = customerData && customerData[0] ? customerData[0].id_customer : null;
      } else {
        const existingCust = customers.find(c => c.nama_pelanggan.toLowerCase() === formData.nama_pengirim.toLowerCase() || (formData.no_wa_pengirim && c.no_wa === formData.no_wa_pengirim));
        if (existingCust) {
          finalCustomerId = existingCust.id_customer;
          const updatedCust = { ...existingCust, total_kiriman: (existingCust.total_kiriman || 0) + 1 };
          setCustomers(customers.map(c => c.id_customer === existingCust.id_customer ? updatedCust : c));
        } else {
          const nextIdNum = customers.length + 1;
          finalCustomerId = `CUST-${nextIdNum < 10 ? '00'+nextIdNum : nextIdNum < 100 ? '0'+nextIdNum : nextIdNum}`;
          const newCust = { id_customer: finalCustomerId, nama_pelanggan: formData.nama_pengirim, no_ktp: formData.no_ktp_pengirim || '-', no_wa: formData.no_wa_pengirim || '-', total_kiriman: 1 };
          setCustomers([...customers, newCust]);
        }
      }
    }

    const selectedAsuransi = layananTambahanList.find(t => t.id_price_asuransi?.toString() === formData.asuransi_id);
    const selectedDTD = layananDtdList.find(t => t.id_price_dtd?.toString() === formData.door_to_door_id);

    const newOrder = {
      id_order: newResi,
      id_pengirim: session?.user?.id || 'offline-admin-id', 
      id_customer: finalCustomerId, 
      nama_pengirim: formData.nama_pengirim || 'Pelanggan Baru', 
      no_ktp_pengirim: formData.no_ktp_pengirim || '-', 
      no_wa_pengirim: formData.no_wa_pengirim || '-',
      alamat_pengirim: formData.alamat_pengirim || '-',
      nama_penerima: formData.nama_penerima || '-', 
      no_wa_penerima: formData.no_wa_penerima || '-', 
      alamat_penerima: formData.alamat_penerima || '-',
      merk_tipe_kendaraan: formData.merk_tipe_kendaraan || 'Kendaraan Baru', 
      warna_kendaraan: formData.warna_kendaraan || '-', 
      nomor_polisi: formData.nomor_polisi || '-', 
      tahun_kendaraan: parseInt(formData.tahun_kendaraan) || 2026, 
      golongan_kendaraan: formData.golongan_kendaraan || '-', 
      catatan_kendaraan: formData.catatan_kendaraan || '-',
      origin: formData.origin, 
      destination: formData.destination, 
      tanggal_muat: formData.tanggal_muat,
      harga_dasar_snapshot: parseFloat(formData.harga_dasar_snapshot) || 0, 
      layanan_asuransi: !!selectedAsuransi, 
      layanan_door_to_door: !!selectedDTD, 
      asuransi_id: formData.asuransi_id ? Number(formData.asuransi_id) : null,
      door_to_door_id: formData.door_to_door_id ? Number(formData.door_to_door_id) : null,
      harga_asuransi: selectedAsuransi ? Number(selectedAsuransi.harga_asuransi) : 0,
      harga_door_to_door: selectedDTD ? Number(selectedDTD.harga_dtd) : 0,
      status_muatan: 'Menunggu', 
      status_payment: 'Belum',
      catatan_pengiriman: formData.catatan_pengiriman || '',
      created_at: new Date().toISOString()
    };

    if (supabaseClient) {
      try {
        const { error: orderError } = await supabaseClient.from('orders').insert([newOrder]);
        if (orderError) {
          setSubmitError(`Akses Ditolak/Kolom Missing di Supabase: ${orderError.message}. Pastikan sudah menjalankan Script Update SQL di tab Pengaturan!`);
          setIsSubmitting(false);
          return;
        }
        tarikDataDatabase();
      } catch (err) {
        setSubmitError(`Koneksi gagal: ${err.message}`);
        setIsSubmitting(false);
        return;
      }
    } else {
      setOrders([newOrder, ...orders]);
    }

    setIsSubmitting(false);
    setSubmitSuccess(true);
    setGeneratedResi(newResi);
    
    if (mainContentRef.current) mainContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => {
    setSubmitSuccess(false);
    setGeneratedResi('');
    setSubmitError('');
    setFormData({ 
      nama_pengirim: '', no_ktp_pengirim: '', no_wa_pengirim: '', alamat_pengirim: '',
      nama_penerima: '', no_wa_penerima: '', alamat_penerima: '', 
      merk_tipe_kendaraan: '', warna_kendaraan: '', nomor_polisi: '', tahun_kendaraan: '', golongan_kendaraan: '', catatan_kendaraan: '', 
      origin: '', destination: '', harga_dasar_snapshot: '', tanggal_muat: '', 
      asuransi_id: '', door_to_door_id: '',
      catatan_pengiriman: ''
    });
  };

  const openEditModal = (order) => {
    let localISOTime;
    if (order.tracking_updated_at) {
      const d = new Date(order.tracking_updated_at);
      const tzOffset = d.getTimezoneOffset() * 60000;
      localISOTime = (new Date(d - tzOffset)).toISOString().slice(0, 16);
    } else {
      const now = new Date();
      const tzOffset = now.getTimezoneOffset() * 60000;
      localISOTime = (new Date(now - tzOffset)).toISOString().slice(0, 16);
    }

    setEditingOrder({ 
      ...order,
      asuransi_id: order.asuransi_id || '',
      door_to_door_id: order.door_to_door_id || '',
      tanggal_update_status: localISOTime
    });
    setEditError('');
    setTrackingPhotos([]);
    setIsUploadingPhotos(false);
    setIsEditModalOpen(true);
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setEditingOrder({ ...editingOrder, [name]: checked });
    } else if (name === 'no_wa_pengirim' || name === 'no_ktp_pengirim' || name === 'no_wa_penerima' || name === 'tahun_kendaraan' || name === 'harga_dasar_snapshot') {
      setEditingOrder({ ...editingOrder, [name]: value.replace(/[^0-9]/g, '') });
    } else {
      setEditingOrder({ ...editingOrder, [name]: value });
    }
  };

  useEffect(() => {
    if (editingOrder && editingOrder.origin && editingOrder.destination && editingOrder.golongan_kendaraan) {
      if (editingOrder.origin === editingOrder.destination) {
        if (editingOrder.harga_dasar_snapshot !== 0) setEditingOrder(prev => ({ ...prev, harga_dasar_snapshot: 0 }));
      } else {
        let calculatedPrice = 0;
        const exactRate = golonganList.find(r => r.origin === editingOrder.origin && r.destination === editingOrder.destination && r.golongan === editingOrder.golongan_kendaraan);

        if (exactRate && exactRate.harga_dasar) {
          calculatedPrice = Number(exactRate.harga_dasar);
        } else {
          const basePrice = 3500000;
          const stringVar = editingOrder.origin.charCodeAt(0) + editingOrder.destination.charCodeAt(0);
          const lengthVar = Math.abs(editingOrder.origin.length - editingOrder.destination.length) * 100000;
          calculatedPrice = basePrice + (stringVar * 10000) + lengthVar;
          calculatedPrice = Math.round(calculatedPrice / 50000) * 50000; 
        }
        
        const selectedAsuransiEdit = layananTambahanList.find(t => t.id_price_asuransi?.toString() === editingOrder.asuransi_id?.toString());
        if (selectedAsuransiEdit) calculatedPrice += Number(selectedAsuransiEdit.harga_asuransi);

        const selectedDTDEdit = layananDtdList.find(t => t.id_price_dtd?.toString() === editingOrder.door_to_door_id?.toString());
        if (selectedDTDEdit) calculatedPrice += Number(selectedDTDEdit.harga_dtd);

        if (editingOrder.harga_dasar_snapshot !== calculatedPrice) {
          setEditingOrder(prev => ({ ...prev, harga_dasar_snapshot: calculatedPrice }));
        }
      }
    }
  }, [editingOrder?.origin, editingOrder?.destination, editingOrder?.asuransi_id, editingOrder?.door_to_door_id, editingOrder?.golongan_kendaraan, golonganList, layananTambahanList, layananDtdList]);

  const saveEditOrder = async (e) => {
    e.preventDefault();
    setEditError('');
    setIsSubmitting(true);
    setIsUploadingPhotos(true);
    
    const selectedAsuransiEdit = layananTambahanList.find(t => t.id_price_asuransi?.toString() === editingOrder.asuransi_id?.toString());
    const selectedDTDEdit = layananDtdList.find(t => t.id_price_dtd?.toString() === editingOrder.door_to_door_id?.toString());

    const finalizedOrder = { 
      ...editingOrder, 
      harga_dasar_snapshot: Number(editingOrder.harga_dasar_snapshot),
      layanan_asuransi: !!editingOrder.asuransi_id,
      layanan_door_to_door: !!editingOrder.door_to_door_id,
      asuransi_id: editingOrder.asuransi_id ? Number(editingOrder.asuransi_id) : null,
      door_to_door_id: editingOrder.door_to_door_id ? Number(editingOrder.door_to_door_id) : null,
      harga_asuransi: selectedAsuransiEdit ? Number(selectedAsuransiEdit.harga_asuransi) : 0,
      harga_door_to_door: selectedDTDEdit ? Number(selectedDTDEdit.harga_dtd) : 0,
      tracking_updated_at: editingOrder.tanggal_update_status ? new Date(editingOrder.tanggal_update_status).toISOString() : null
    };
    
    const { tanggal_update_status, ...orderToUpdateSupabase } = finalizedOrder;

    let uploadedPhotoUrls = [];
    const originalOrderInfo = orders.find(o => o.id_order === editingOrder.id_order);
    const isStatusChanged = originalOrderInfo && originalOrderInfo.status_muatan !== finalizedOrder.status_muatan;
    const isCatatanAdded = finalizedOrder.catatan_pengiriman && finalizedOrder.catatan_pengiriman.trim() !== '';

    if (supabaseClient) {
      if (trackingPhotos.length > 0) {
        // Format status untuk nama folder (misal: "Proses Muat" -> "proses-muat")
        const statusFolder = finalizedOrder.status_muatan 
          ? finalizedOrder.status_muatan.toLowerCase().replace(/\s+/g, '-') 
          : 'menunggu';

        for (let i = 0; i < trackingPhotos.length; i++) {
          const fileObj = trackingPhotos[i];
          const fileName = `${editingOrder.id_order}/${statusFolder}/${Date.now()}-${i}.webp`;
          
          try {
            const { data: uploadData, error: uploadError } = await supabaseClient.storage
              .from('tracking-photos')
              .upload(fileName, fileObj.file, { 
                upsert: true,
                contentType: 'image/webp',
                cacheControl: '3600'
              });
              
            if (uploadError) throw uploadError;
            uploadedPhotoUrls.push(uploadData.path);
          } catch (err) {
            console.error("Gagal upload foto tracking:", err);
            setEditError(`Upload foto gagal: ${err.message === 'Failed to fetch' ? 'Pastikan Bucket "tracking-photos" berstatus Public dan Policy/CORS sudah diatur.' : err.message}`);
            setIsSubmitting(false);
            setIsUploadingPhotos(false);
            return; // Hentikan penyimpanan jika upload gagal
          }
        }
      }

      const { error } = await supabaseClient.from('orders').update(orderToUpdateSupabase).eq('id_order', finalizedOrder.id_order);
      if (error) {
        setEditError(`Gagal Update di Supabase: ${error.message}`);
        setIsSubmitting(false);
        setIsUploadingPhotos(false);
        return;
      }

      if (isStatusChanged || uploadedPhotoUrls.length > 0 || isCatatanAdded) {
         const trackingLog = {
           id_order: finalizedOrder.id_order,
           status: finalizedOrder.status_muatan,
           catatan: finalizedOrder.catatan_pengiriman || '-',
           photo_urls: uploadedPhotoUrls,
           created_at: new Date(tanggal_update_status || new Date()).toISOString()
         };
         await supabaseClient.from('tracking_history').insert([trackingLog]);
      }
    }

    const originalOrder = orders.find(o => o.id_order === editingOrder.id_order);
    const isSenderChanged = originalOrder && (
      originalOrder.nama_pengirim !== finalizedOrder.nama_pengirim || originalOrder.no_wa_pengirim !== finalizedOrder.no_wa_pengirim || originalOrder.no_ktp_pengirim !== finalizedOrder.no_ktp_pengirim
    );

    setOrders(prevOrders => prevOrders.map(order => {
      if (order.id_order === finalizedOrder.id_order) return finalizedOrder;
      if (isSenderChanged && (order.nama_pengirim === originalOrder.nama_pengirim || order.no_wa_pengirim === originalOrder.no_wa_pengirim)) {
        const syncedOrder = { ...order, nama_pengirim: finalizedOrder.nama_pengirim, no_wa_pengirim: finalizedOrder.no_wa_pengirim, no_ktp_pengirim: finalizedOrder.no_ktp_pengirim };
        if (supabaseClient) supabaseClient.from('orders').update(syncedOrder).eq('id_order', order.id_order);
        return syncedOrder;
      }
      return order;
    }));

    if (isSenderChanged) {
      setCustomers(prevCustomers => prevCustomers.map(cust => {
        if (cust.nama_pelanggan === originalOrder.nama_pengirim || cust.no_wa === originalOrder.no_wa_pengirim) {
          const syncedCust = { ...cust, nama_pelanggan: finalizedOrder.nama_pengirim, no_wa: finalizedOrder.no_wa_pengirim, no_ktp: finalizedOrder.no_ktp_pengirim };
          if (supabaseClient) supabaseClient.from('customers').update(syncedCust).eq('id_customer', cust.id_customer);
          return syncedCust;
        }
        return cust;
      }));
    }

    setIsSubmitting(false);
    setIsUploadingPhotos(false);
    setIsEditModalOpen(false);
    setEditingOrder(null);
    setTrackingPhotos([]);
  };

  const openDeleteModal = (order) => {
    setOrderToDelete(order);
    setIsDeleteModalOpen(true);
  };
  const confirmDeleteOrder = async () => {
    if (orderToDelete) {
      setOrders(orders.filter(order => order.id_order !== orderToDelete.id_order));
      if (supabaseClient) {
        await supabaseClient.from('orders').delete().eq('id_order', orderToDelete.id_order);
      }
      setIsDeleteModalOpen(false);
      setOrderToDelete(null);
    }
  };

  const openChecklistModal = (order) => {
    setSelectedChecklistOrder(order);
    
    // Inisialisasi struktur data checklist jika belum ada
    const initialItems = CHECKLIST_ITEMS.reduce((acc, item) => ({ 
      ...acc, 
      [item]: { ada: false, tidak_ada: false, jml: '', status: '' } 
    }), {});

    setChecklistFormData(order.checklist_data || {
      no_rangka: '', no_mesin: '', no_surat_jalan: '', km_speedometer: '',
      items: initialItems,
      catatan_body: order.catatan_kendaraan || '',
      parts_damaged: []
    });
    setIsChecklistModalOpen(true);
  };

  const handleChecklistChange = (item, field, value) => {
    setChecklistFormData(prev => {
      // Logic khusus: jika 'ada' dicentang, 'tidak_ada' otomatis false (dan sebaliknya)
      let updatedItem = { ...prev.items[item], [field]: value };
      if (field === 'ada' && value) updatedItem.tidak_ada = false;
      if (field === 'tidak_ada' && value) updatedItem.ada = false;

      return {
        ...prev,
        items: { ...prev.items, [item]: updatedItem }
      };
    });
  };

  const togglePartDamage = (part) => {
    setChecklistFormData(prev => {
      const exists = prev.parts_damaged.includes(part);
      return {
        ...prev,
        parts_damaged: exists 
          ? prev.parts_damaged.filter(p => p !== part)
          : [...prev.parts_damaged, part]
      };
    });
  };

  const saveChecklist = async () => {
    const updatedOrder = { ...selectedChecklistOrder, checklist_data: checklistFormData };
    setOrders(orders.map(o => o.id_order === updatedOrder.id_order ? updatedOrder : o));
    
    // Note: Jika menggunakan Supabase, Anda harus menambahkan kolom JSONB 'checklist_data' pada tabel 'orders'
    if (supabaseClient) {
      try {
        await supabaseClient.from('orders').update({ checklist_data: checklistFormData }).eq('id_order', updatedOrder.id_order);
      } catch (e) {
        console.warn("Gagal update DB. Pastikan kolom checklist_data (tipe JSONB) sudah dibuat di Supabase.", e);
      }
    }
    
    setIsChecklistModalOpen(false);
  };

  const handlePrintChecklist = (order, checklist) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const items = checklist.items || {};
    const halfLength = Math.ceil(CHECKLIST_ITEMS.length / 2);
    let tableRows = '';
    
    for(let i = 0; i < halfLength; i++) {
       const item1 = CHECKLIST_ITEMS[i];
       const item2 = CHECKLIST_ITEMS[i + halfLength];
       const state1 = items[item1] || {};
       const state2 = item2 ? (items[item2] || {}) : null;

       tableRows += `<tr>
          <td class="text-center">${i + 1}</td>
          <td>${item1}</td>
          <td class="text-center font-bold">${state1.ada ? 'V' : ''}</td>
          <td class="text-center font-bold">${state1.tidak_ada ? 'X' : ''}</td>
          <td class="text-center">${state1.jml || ''}</td>
          <td>${state1.status || ''}</td>
          
          <td class="text-center">${item2 ? (i + 1 + halfLength) : ''}</td>
          <td>${item2 || ''}</td>
          <td class="text-center font-bold">${state2 ? (state2.ada ? 'V' : '') : ''}</td>
          <td class="text-center font-bold">${state2 ? (state2.tidak_ada ? 'X' : '') : ''}</td>
          <td class="text-center">${state2 ? (state2.jml || '') : ''}</td>
          <td>${state2 ? (state2.status || '') : ''}</td>
       </tr>`;
    }

    const partNames = {
      'bumper_depan': 'Bumper Depan', 'kap_depan': 'Kap Depan', 'kaca_depan': 'Kaca Depan',
      'atap': 'Atap', 'kaca_belakang': 'Kaca Belakang', 'bagasi': 'Bagasi / Kap Belakang',
      'bumper_blkg': 'Bumper Belakang', 'pintu_kiri_dpn': 'Pintu Kiri Depan', 'pintu_kiri_blkg': 'Pintu Kiri Belakang',
      'pintu_kanan_dpn': 'Pintu Kanan Depan', 'pintu_kanan_blkg': 'Pintu Kanan Belakang'
    };
    
    const partsArray = checklist.parts_damaged || [];
    const damagedText = partsArray.length > 0 
      ? partsArray.map(p => partNames[p]).join(', ') 
      : 'Tidak ada kerusakan body yang ditandai.';

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Vehicle Checklist - AntarOto.com - ${order.id_order}</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; font-size: 11px; margin: 30px; color: #000; }
            .header-cont { text-align: center; margin-bottom: 25px; }
            .header { font-weight: bold; font-size: 22px; text-decoration: underline; margin-bottom: 5px; }
            .sub-header { font-weight: bold; font-size: 14px; }
            .info-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 12px; }
            .info-table td { padding: 4px; vertical-align: top; }
            .info-table td.label { font-weight: bold; width: 100px; }
            .vehicle-box { border: 2px solid #000; padding: 10px; margin-bottom: 20px; }
            .check-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 11px; }
            .check-table th, .check-table td { border: 1px solid #000; padding: 5px; }
            .check-table th { background-color: #f0f0f0; text-align: center; }
            .text-center { text-align: center; }
            .font-bold { font-weight: bold; }
            .box { border: 1px solid #000; padding: 10px; min-height: 40px; margin-top: 5px; }
            .signatures { width: 100%; margin-top: 40px; display: table; table-layout: fixed; }
            .sig-col { display: table-cell; text-align: center; font-size: 10px; font-weight: bold; }
            .sig-line { margin-top: 70px; border-bottom: 1px solid #000; width: 80%; margin-left: 10%; }
            @media print { 
              @page { margin: 1cm; }
              body { margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; } 
            }
          </style>
        </head>
        <body>
          <div class="header-cont">
            <div class="header">VEHICLE CHECKLIST</div>
            <div class="sub-header">BUKTI SERAH TERIMA KENDARAAN</div>
          </div>
          
          <table style="width: 100%; margin-bottom: 15px;">
            <tr>
              <td style="width: 50%; vertical-align: top;">
                <strong>PENGIRIM</strong>
                <table class="info-table">
                  <tr><td class="label">NAMA</td><td>: ${order.nama_pengirim || '-'}</td></tr>
                  <tr><td class="label">ALAMAT</td><td>: ${order.alamat_pengirim || '-'}</td></tr>
                  <tr><td class="label">TELP/HP</td><td>: ${order.no_wa_pengirim || '-'}</td></tr>
                </table>
              </td>
              <td style="width: 50%; vertical-align: top;">
                <strong>PENERIMA</strong>
                <table class="info-table">
                  <tr><td class="label">NAMA</td><td>: ${order.nama_penerima || '-'}</td></tr>
                  <tr><td class="label">ALAMAT</td><td>: ${order.alamat_penerima || '-'}</td></tr>
                  <tr><td class="label">TELP/HP</td><td>: ${order.no_wa_penerima || '-'}</td></tr>
                </table>
              </td>
            </tr>
          </table>

          <div class="vehicle-box">
             <table style="width: 100%;">
               <tr>
                 <td style="width: 50%; vertical-align: top;">
                   <table class="info-table" style="margin:0;">
                     <tr><td class="label">MERK/TYPE</td><td>: ${order.merk_tipe_kendaraan || '-'}</td></tr>
                     <tr><td class="label">NO. POLISI</td><td style="text-transform: uppercase;">: ${order.nomor_polisi || '-'}</td></tr>
                     <tr><td class="label">WARNA</td><td>: ${order.warna_kendaraan || '-'}</td></tr>
                   </table>
                 </td>
                 <td style="width: 50%; vertical-align: top;">
                   <table class="info-table" style="margin:0;">
                     <tr><td class="label">NO. RANGKA</td><td style="text-transform: uppercase;">: ${checklist.no_rangka || '-'}</td></tr>
                     <tr><td class="label">NO. MESIN</td><td style="text-transform: uppercase;">: ${checklist.no_mesin || '-'}</td></tr>
                     <tr><td class="label">SPEEDOMETER</td><td>: ${checklist.km_speedometer || '-'}</td></tr>
                   </table>
                 </td>
               </tr>
             </table>
          </div>

          <strong>INFORMASI PERLENGKAPAN KENDARAAN</strong>
          <table class="check-table" style="margin-top: 8px;">
            <thead>
              <tr>
                <th style="width: 4%">NO</th>
                <th style="width: 22%">PERLENGKAPAN</th>
                <th style="width: 4%">ADA</th>
                <th style="width: 4%">TDK</th>
                <th style="width: 6%">JML</th>
                <th style="width: 10%">STATUS</th>
                
                <th style="width: 4%">NO</th>
                <th style="width: 22%">PERLENGKAPAN</th>
                <th style="width: 4%">ADA</th>
                <th style="width: 4%">TDK</th>
                <th style="width: 6%">JML</th>
                <th style="width: 10%">STATUS</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>

          <table style="width: 100%; margin-top: 20px;">
            <tr>
              <td style="width: 50%; vertical-align: top; padding-right: 10px;">
                 <strong>Pemeriksaan Body (Bagian Rusak):</strong>
                 <div class="box">${damagedText}</div>
              </td>
              <td style="width: 50%; vertical-align: top; padding-left: 10px;">
                 <strong>Catatan Tambahan:</strong>
                 <div class="box">${checklist.catatan_body || '-'}</div>
              </td>
            </tr>
          </table>

          <div style="font-size: 10px; margin-top: 15px; color: #555;">
            * Segala kerusakan/kerugian dalam proses pengiriman kendaraan yang diakibatkan oleh Force Majeur (kecelakaan, bencana alam, dll) bukan merupakan tanggung jawab pihak ekspedisi.
          </div>

          <div class="signatures">
            <div class="sig-col">
              PENGIRIM
              <div class="sig-line"></div>
            </div>
            <div class="sig-col">
              SUPIR
              <div class="sig-line"></div>
            </div>
            <div class="sig-col">
              PETUGAS ORIGIN
              <div class="sig-line"></div>
            </div>
            <div class="sig-col">
              PETUGAS DESTINASI
              <div class="sig-line"></div>
            </div>
            <div class="sig-col">
              PENERIMA
              <div class="sig-line"></div>
            </div>
          </div>
          
          <script>window.onload = function() { setTimeout(function() { window.print(); }, 300); }</script>
        </body>
      </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const options = { day: 'numeric', month: 'short', year: 'numeric' };
    return new Date(dateStr).toLocaleDateString('id-ID', options);
  };

  const filteredOrders = orders.filter(order => {
    const query = searchQuery.toLowerCase();
    const routeName = (order.origin + ' - ' + order.destination).toLowerCase();
    return (
      order.id_order?.toLowerCase().includes(query) ||
      order.nama_pengirim?.toLowerCase().includes(query) ||
      order.status_muatan?.toLowerCase().includes(query) ||
      order.merk_tipe_kendaraan?.toLowerCase().includes(query) ||
      routeName.includes(query)
    );
  });

  const filteredCustomers = customers.filter(cust => {
    const query = searchQuery.toLowerCase();
    return (
      cust.id_customer?.toLowerCase().includes(query) ||
      cust.nama_pelanggan?.toLowerCase().includes(query) ||
      cust.no_ktp?.toLowerCase().includes(query) ||
      cust.no_wa?.toLowerCase().includes(query)
    );
  });

  const filteredTarif = golonganList.filter(t => {
    const query = searchQuery.toLowerCase();
    return (
      t.origin?.toLowerCase().includes(query) ||
      t.destination?.toLowerCase().includes(query) ||
      t.golongan?.toLowerCase().includes(query)
    );
  });

  const filteredTambahan = layananTambahanList.filter(t => {
    const query = searchQuery.toLowerCase();
    return (
      t.nama_layanan?.toLowerCase().includes(query) ||
      t.tipe_kendaraan?.toLowerCase().includes(query)
    );
  });

  const filteredDtd = layananDtdList.filter(t => {
    const query = searchQuery.toLowerCase();
    return (
      t.nama_layanan?.toLowerCase().includes(query) ||
      t.tipe_kendaraan?.toLowerCase().includes(query)
    );
  });

  const renderStatusBadge = (status) => {
    let bgClass = 'bg-slate-100 text-slate-700';
    let dotClass = 'bg-slate-500';
    if (status === 'Menunggu') { bgClass = 'bg-amber-100 text-amber-700'; dotClass = 'bg-amber-500'; } 
    else if (status === 'Proses Muat') { bgClass = 'bg-blue-100 text-blue-700'; dotClass = 'bg-blue-500'; } 
    else if (status === 'Dalam Perjalanan') { bgClass = 'bg-indigo-100 text-indigo-700'; dotClass = 'bg-indigo-500'; } 
    else if (status === 'Tiba di Tujuan') { bgClass = 'bg-purple-100 text-purple-700'; dotClass = 'bg-purple-500'; } 
    else if (status === 'Selesai') { bgClass = 'bg-emerald-100 text-emerald-700'; dotClass = 'bg-emerald-500'; }

    return (
      <span className={`px-2.5 py-1 rounded-full text-[11px] font-medium inline-flex items-center gap-1.5 ${bgClass}`}>
        <div className={`w-1.5 h-1.5 rounded-full ${dotClass}`}></div>{status}
      </span>
    );
  };

  const renderPaymentBadge = (status) => {
    if (status === 'Lunas') return <span className="font-bold text-emerald-600">Lunas</span>;
    return <span className="font-bold text-red-600">Belum</span>;
  };

  const handlePrintInvoice = (order) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    
    const insuranceCost = order.harga_asuransi !== undefined ? Number(order.harga_asuransi) : ((order.insurance || order.layanan_asuransi) ? 300000 : 0);
    const doorToDoorCost = order.harga_door_to_door !== undefined ? Number(order.harga_door_to_door) : ((order.doorToDoor || order.layanan_door_to_door) ? 500000 : 0);
    
    const basePrice = (Number(order.price || order.harga_dasar_snapshot) || 0) - insuranceCost - doorToDoorCost;

    const insuranceHtml = insuranceCost > 0 ? `<tr><td class="label">Asuransi</td><td class="amount">Rp ${insuranceCost.toLocaleString('id-ID')}</td></tr>` : '';
    const doorToDoorHtml = doorToDoorCost > 0 ? `<tr><td class="label">Door to Door</td><td class="amount">Rp ${doorToDoorCost.toLocaleString('id-ID')}</td></tr>` : '';

    const isLunas = order.paymentStatus === 'Lunas' || order.status_payment === 'Lunas' || order.status_payment === 'lunas';
    const stampHtml = isLunas ? '<div class="watermark-stamp">LUNAS</div>' : '';

    const totalNominal = Number(order.price || order.harga_dasar_snapshot) || 0;
    const teksTerbilang = totalNominal > 0 ? `${terbilang(totalNominal)} Rupiah` : 'Nol Rupiah';

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice - ${order.id || order.id_order}</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 40px; color: #333; line-height: 1.6; position: relative; }
            .header { border-bottom: 2px solid #2563eb; padding-bottom: 20px; margin-bottom: 30px; display: flex; justify-content: space-between; align-items: flex-end; }
            .title { font-size: 28px; font-weight: bold; color: #1e3a8a; margin: 0; }
            .subtitle { font-size: 14px; color: #64748b; }
            .resi-box { text-align: right; }
            .resi-label { font-size: 12px; color: #64748b; text-transform: uppercase; letter-spacing: 1px; }
            .resi-number { font-size: 20px; font-weight: bold; color: #0f172a; margin-bottom: 4px; }
            .invoice-date { font-size: 12px; color: #64748b; }
            .customer-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 30px; }
            .customer-section-title { font-size: 14px; font-weight: bold; color: #1e3a8a; border-bottom: 2px solid #e2e8f0; padding-bottom: 6px; margin-bottom: 12px; text-transform: uppercase; }
            .info-table { width: 100%; }
            .info-table td { padding: 4px 0; vertical-align: top; font-size: 10pt; }
            .info-table td:first-child { width: 95px; font-weight: 600; color: #475569; }
            .bordered-table { width: 100%; border-collapse: collapse; margin-bottom: 30px; font-size: 10pt; }
            .bordered-table th, .bordered-table td { border: 1px solid #cbd5e1; padding: 10px 14px; text-align: left; }
            .bordered-table th { background-color: #f1f5f9; color: #334155; font-weight: bold; }
            .cost-table { width: 100%; }
            .cost-table td.label { font-weight: 600; color: #475569; }
            .cost-table td.amount { text-align: right; min-width: 140px; font-weight: bold; }
            .cost-table tr.total th, .cost-table tr.total td { background-color: #eff6ff; font-size: 10pt; font-weight: bold; color: #1e3a8a; }
            .footer-contact { position: fixed; bottom: 2.5cm; left: 1.6cm; right: 1.6cm; text-align: center; font-size: 8pt; color: #64748b; border-top: 0.5pt solid #cbd5e1; padding-top: 8px; }
            .tnc-page { page-break-before: always; font-size: 9pt; margin-top: 3.5cm; color: #334155; }
            .tnc-title { font-weight: bold; font-size: 11pt; color: #1e3a8a; margin-bottom: 15px; text-align: center; }
            .tnc-list { padding-left: 20px; margin: 0; }
            .tnc-list li { margin-bottom: 8px; }
            .tnc-sublist { list-style-type: disc; padding-left: 20px; margin-top: 4px; margin-bottom: 4px; }
            .watermark-stamp {
              position: absolute; top: 35%; left: 50%; transform: translate(-50%, -50%) rotate(-30deg);
              font-size: 90px; font-weight: 900; color: rgba(191, 219, 254, 0.35); border: 12px double rgba(191, 219, 254, 0.45);
              padding: 10px 40px; border-radius: 20px; letter-spacing: 10px; pointer-events: none; z-index: 9999; user-select: none;
            }
            @media print { 
              @page { margin: 0; } 
              body { margin: 1.6cm; margin-bottom: 4cm; -webkit-print-color-adjust: exact; print-color-adjust: exact; } 
              .no-print { display: none; }
              .watermark-stamp { color: rgba(191, 219, 254, 0.35) !important; border-color: rgba(191, 219, 254, 0.45) !important; }
            }
          </style>
        </head>
        <body>
          
          ${stampHtml}

          <div class="header">
            <div><h1 class="title">AntarOto.com</h1><div class="subtitle">Layanan Pengiriman Kendaraan Terpercaya</div></div>
            <div class="resi-box"><div class="resi-label">Nomor Resi / AWB</div><div class="resi-number">${order.id || order.id_order}</div><div class="invoice-date">Tanggal: <strong>${formatDate(order.entryDate || order.created_at)}</strong></div></div>
          </div>
          <div class="customer-grid">
            <div>
              <div class="customer-section-title">Data Pengirim</div>
              <table class="info-table"><tr><td>Nama</td><td>: ${order.nama_pengirim || '-'}</td></tr><tr><td>No. Telp</td><td>: ${order.no_wa_pengirim || '-'}</td></tr><tr><td>Alamat</td><td>: ${order.alamat_pengirim || '-'}</td></tr></table>
            </div>
            <div>
              <div class="customer-section-title">Data Penerima</div>
              <table class="info-table"><tr><td>Nama</td><td>: ${order.nama_penerima || '-'}</td></tr><tr><td>No. Telp</td><td>: ${order.no_wa_penerima || '-'}</td></tr><tr><td>Alamat</td><td>: ${order.alamat_penerima || '-'}</td></tr></table>
            </div>
          </div>
          <table class="bordered-table"><thead><tr><th>Pelabuhan Asal</th><th>Pelabuhan Tujuan</th></tr></thead><tbody><tr><td>${order.origin || '-'}</td><td>${order.destination || '-'}</td></tr></tbody></table>
          <table class="bordered-table"><thead><tr><th>Merk & Model</th><th>Golongan</th><th>Warna</th><th>No. Polisi</th><th>Tahun</th><th>Catatan Fisik</th></tr></thead><tbody><tr><td>${order.merk_tipe_kendaraan || '-'}</td><td>${order.golongan_kendaraan || '-'}</td><td>${order.warna_kendaraan || '-'}</td><td style="text-transform: uppercase;">${order.nomor_polisi || '-'}</td><td>${order.tahun_kendaraan || '-'}</td><td>${order.catatan_kendaraan || '-'}</td></tr></tbody></table>
          <div style="display: flex; justify-content: space-between; align-items: flex-end; margin-top: 30px;">
            <div style="border: 1px dashed #cbd5e1; padding: 15px; border-radius: 8px; background-color: #f8fafc; font-size: 10pt; max-width: 250px;">
              <div style="font-weight: bold; color: #1e3a8a; margin-bottom: 5px;">Informasi Pembayaran:</div>
              <div>Pembayaran dengan transfer bank:</div><div style="font-weight: bold; font-size: 11pt; margin: 4px 0;">BCA: 390-038-1777</div><div>Atas Nama: M. Taufik S.Kom</div>
            </div>
            <div style="width: 50%; margin-left: auto;">
              <table class="bordered-table cost-table" style="margin-bottom: 8px;">
                <tbody>
                  <tr>
                    <td class="label">Biaya Kirim</td>
                    <td class="amount">Rp ${basePrice.toLocaleString('id-ID')}</td>
                  </tr>
                  ${insuranceHtml}
                  ${doorToDoorHtml}
                  <tr class="total">
                    <td class="label">Total Biaya</td>
                    <td class="amount">Rp ${totalNominal.toLocaleString('id-ID')}</td>
                  </tr>
                </tbody>
              </table>
              
              <div style="text-align: right; font-size: 9.5pt; font-style: italic; color: #475569; margin-top: 6px; padding-right: 14px;">
                <strong>Terbilang:</strong> ${teksTerbilang}
              </div>
            </div>
          </div>
          <div class="footer-contact">Kontak: 0813-555-33802 &nbsp;|&nbsp; Email: admin@antaroto.com &nbsp;|&nbsp; Alamat: Pelabuhan Soekarno-Hatta, Makassar</div>
          <div class="tnc-page">
            <div class="tnc-title">TERM & CONDITION PENGIRIMAN KENDARAAN<br>- AntarOto.com -</div>
            <ol class="tnc-list">
              <li>Kendaraan wajib dilengkapi dokumen yang sah, seperti STNK atau surat keterangan kepemilikan yang berlaku.</li>
              <li>BBM maksimal 1/4 tangki demi keamanan selama proses pengiriman.</li>
              <li>Barang pribadi berharga seperti uang, perhiasan, laptop, handphone, surat penting, dan dokumen berharga tidak diperkenankan ditinggalkan di dalam kendaraan.</li>
              <li>Pengirim wajib memastikan kendaraan dalam kondisi: <ul class="tnc-sublist"><li>Rem berfungsi normal</li><li>Ban dalam kondisi baik</li><li>Tidak mengalami kebocoran oli atau bahan bakar</li><li>Dapat dihidupkan dan dijalankan</li></ul></li>
              <li>Foto kondisi kendaraan akan dilakukan saat proses serah terima sebagai dokumentasi.</li>
              <li>Estimasi waktu pengiriman bersifat perkiraan dan dapat berubah karena kondisi cuaca, pelayaran, kemacetan, atau faktor operasional lainnya.</li>
              <li>Klaim kerusakan atau kehilangan wajib disampaikan maksimal 1 x 24 jam setelah kendaraan diterima dengan melampirkan bukti pendukung.</li>
              <li>Biaya pengiriman harus dilunasi sesuai kesepakatan sebelum kendaraan diberangkatkan.</li>
              <li>Dengan menyerahkan kendaraan kepada pihak ekspedisi, pelanggan dianggap telah menyetujui seluruh syarat dan ketentuan yang berlaku.</li>
            </ol>
          </div>
          <script>window.onload = function() { setTimeout(function() { window.print(); }, 300); }</script>
        </body>
      </html>
    `;
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  if (loadingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin h-10 w-10 border-4 border-blue-600 border-t-transparent rounded-full"></div>
          <p className="text-slate-500 font-semibold text-xs tracking-wider uppercase">Memuat Sesi Keamanan...</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <>
        <LoginScreen onLogin={handleLoginAttempt} externalError={loginError} onOpenConfig={() => setShowConfigModal(true)} isConnected={!!supabaseClient} />
        {showConfigModal && <SupabaseConfigModal onSave={handleSaveConfig} onClear={handleClearConfig} onClose={() => setShowConfigModal(false)} currentUrl={supabaseUrl} currentKey={supabaseKey} />}
      </>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50 font-sans text-slate-800 overflow-hidden relative pb-16 md:pb-0">
      
      {}
      <aside className={`hidden md:flex flex-col inset-y-0 left-0 z-50 ${isSidebarCollapsed ? 'w-20' : 'w-64'} transition-all duration-300 ease-in-out bg-slate-900 text-slate-300 flex-shrink-0 shadow-2xl relative`}>
        <button onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)} className="absolute -right-3 top-20 bg-blue-600 text-white rounded-full p-1 shadow-md hover:bg-blue-500 transition-colors z-50 border-2 border-slate-900">
          {isSidebarCollapsed ? <ChevronRight size={14} strokeWidth={3} /> : <ChevronLeft size={14} strokeWidth={3} />}
        </button>

        <div className="h-16 md:h-20 flex items-center justify-center px-4 border-b border-slate-800 shrink-0">
          <div className="flex items-center w-full justify-center">
            <img src="logo-antaroto.png" alt="AntarOto Logo" className="h-8 md:h-10 w-auto" />
            {!isSidebarCollapsed && (
              <div className="ml-3 transition-opacity duration-300">
                <span className="text-lg md:text-xl font-bold text-white block leading-tight">AntarOto</span>
                <span className="text-[9px] md:text-[10px] text-blue-400 font-semibold tracking-wider uppercase block">Admin Portal</span>
                <span className="text-[10px] md:text-[11px] text-white block mt-0.5">v.1.1-beta</span>
              </div>
            )}
          </div>
        </div>

        {}
        <nav className="flex-1 py-6 px-3 space-y-2 overflow-y-auto custom-scrollbar">
          {!isSidebarCollapsed && <p className="px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Menu Utama</p>}
          <button onClick={() => changeMenu('dashboard')} className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all ${activeMenu === 'dashboard' ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/50' : 'hover:bg-slate-800 hover:text-white'} ${isSidebarCollapsed ? 'justify-center' : ''}`}>
            <LayoutDashboard size={20} className="shrink-0" />{!isSidebarCollapsed && <span className="font-medium text-sm whitespace-nowrap">Dashboard</span>}
          </button>
          <button onClick={() => changeMenu('entry')} className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all ${activeMenu === 'entry' ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/50' : 'hover:bg-slate-800 hover:text-white'} ${isSidebarCollapsed ? 'justify-center' : ''}`}>
            <FilePlus2 size={20} className="shrink-0" />{!isSidebarCollapsed && <span className="font-medium text-sm whitespace-nowrap">Entry Pesanan Baru</span>}
          </button>
          <button onClick={() => changeMenu('tracking')} className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all ${activeMenu === 'tracking' ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/50' : 'hover:bg-slate-800 hover:text-white'} ${isSidebarCollapsed ? 'justify-center' : ''}`}>
            <PackageSearch size={20} className="shrink-0" />{!isSidebarCollapsed && <span className="font-medium text-sm whitespace-nowrap">Update Pelacakan</span>}
          </button>

          {!isSidebarCollapsed && <p className="px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider mt-8 mb-2">Master Data</p>}
          {isSidebarCollapsed && <div className="h-px bg-slate-800 my-4 mx-2"></div>}
          <button onClick={() => changeMenu('customers')} className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all ${activeMenu === 'customers' ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/50' : 'hover:bg-slate-800 hover:text-white text-slate-400'} ${isSidebarCollapsed ? 'justify-center' : ''}`}>
            <Users size={20} className="shrink-0" />{!isSidebarCollapsed && <span className="font-medium text-sm whitespace-nowrap">Data Pelanggan</span>}
          </button>
          <button onClick={() => changeMenu('routes')} className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all ${activeMenu === 'routes' ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/50' : 'hover:bg-slate-800 hover:text-white text-slate-400'} ${isSidebarCollapsed ? 'justify-center' : ''}`}>
            <Route size={20} className="shrink-0" />{!isSidebarCollapsed && <span className="font-medium text-sm whitespace-nowrap">Master Biaya</span>}
          </button>
        </nav>

        <div className="p-3 border-t border-slate-800 shrink-0 space-y-2">
          {currentUser?.role === 'Super Admin' && (
            <button onClick={() => changeMenu('settings')} className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-slate-800 hover:text-white transition-all ${activeMenu === 'settings' ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/50' : 'text-slate-400'} ${isSidebarCollapsed ? 'justify-center' : ''}`}>
              <Settings size={20} className="shrink-0" />{!isSidebarCollapsed && <span className="font-medium text-sm whitespace-nowrap">Pengaturan Sistem</span>}
            </button>
          )}
          <button onClick={handleLogout} className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-red-500/10 hover:text-red-500 transition-all text-slate-400 ${isSidebarCollapsed ? 'justify-center' : ''}`}>
            <LogOut size={20} className="shrink-0" />{!isSidebarCollapsed && <span className="font-medium text-sm whitespace-nowrap">Keluar Sistem</span>}
          </button>
        </div>
      </aside>

      {}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-50 flex items-center justify-between px-2 pb-safe shadow-[0_-4px_15px_rgba(0,0,0,0.03)] h-16">
        <button onClick={() => changeMenu('dashboard')} className={`flex-1 flex flex-col items-center justify-center h-full ${activeMenu === 'dashboard' ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}>
          <Home size={20} className={activeMenu === 'dashboard' ? 'fill-blue-50' : ''} /><span className="text-[10px] font-semibold mt-1">Home</span>
        </button>
        <button onClick={() => changeMenu('entry')} className={`flex-1 flex flex-col items-center justify-center h-full ${activeMenu === 'entry' ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}>
          <FilePlus2 size={20} className={activeMenu === 'entry' ? 'fill-blue-50' : ''} /><span className="text-[10px] font-semibold mt-1">Pesanan</span>
        </button>
        <button onClick={() => changeMenu('tracking')} className={`flex-1 flex flex-col items-center justify-center h-full ${activeMenu === 'tracking' ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}>
          <PackageSearch size={20} className={activeMenu === 'tracking' ? 'fill-blue-50' : ''} /><span className="text-[10px] font-semibold mt-1">Lacak</span>
        </button>
        <button onClick={() => changeMenu('customers')} className={`flex-1 flex flex-col items-center justify-center h-full ${activeMenu === 'customers' ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}>
          <Users size={20} className={activeMenu === 'customers' ? 'fill-blue-50' : ''} /><span className="text-[10px] font-semibold mt-1">Pelanggan</span>
        </button>
        {currentUser?.role === 'Super Admin' && (
          <button onClick={() => changeMenu('settings')} className={`flex-1 flex flex-col items-center justify-center h-full ${activeMenu === 'settings' ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}>
            <Settings size={20} className={activeMenu === 'settings' ? 'fill-blue-50' : ''} /><span className="text-[10px] font-semibold mt-1">Seting</span>
          </button>
        )}
      </nav>

      {/* HEADER UTAMA */}

      {}
      {/* Konten Utama */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="h-16 md:h-20 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-8 flex-shrink-0 z-10 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex flex-col justify-center">
              <span className="text-2xl md:text-3xl font-extrabold text-blue-700 tracking-tight leading-none">AntarOto</span>
              <span className="text-[10px] md:text-[11px] text-slate-500 font-medium mt-1">Jasa Pengiriman Kendaraan Terpercaya</span>
            </div>
          </div>
          <div className="flex items-center gap-3 md:gap-6 shrink-0 ml-4">
            <button onClick={handleLogout} title="Keluar Akun" className="relative p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all">
              <LogOut size={22} />
            </button>
            <div className="h-8 w-px bg-slate-200 hidden md:block"></div>
            <div className="flex items-center gap-2 md:gap-3 cursor-pointer group">
              <div className="text-right hidden md:block">
                <p className="text-sm font-semibold text-slate-700 group-hover:text-blue-600 transition-colors">{currentUser?.name}</p>
                <p className="text-xs text-slate-500">{currentUser?.role}</p>
              </div>
              <UserCircle size={32} className="md:w-9 md:h-9 text-slate-400 group-hover:text-blue-600 transition-colors" />
            </div>
          </div>
        </header>

        {}
        <div ref={mainContentRef} className="flex-1 overflow-y-auto p-4 md:p-8 pb-24 md:pb-8 relative scroll-smooth bg-slate-50">
          
          {}
          {activeMenu === 'entry' && (
            <div className="max-w-5xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500 pb-32 md:pb-0">
              <div className="mb-6 md:mb-8">
                <h1 className="text-xl md:text-2xl font-bold text-slate-900">Entry Pesanan Baru</h1>
                <p className="text-slate-500 text-sm mt-1">Masukkan detail pengiriman untuk membuat Resi baru.</p>
              </div>

              {submitError && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-5 py-4 rounded-2xl text-sm font-semibold flex items-start gap-3 shadow-sm mb-6 animate-in slide-in-from-top-2">
                  <AlertCircle size={20} className="shrink-0 mt-0.5" />
                  <div className="leading-relaxed">
                    <p className="font-bold mb-1">Gagal Menyimpan ke Database</p>
                    <p className="font-medium text-xs text-red-500/90">{submitError}</p>
                  </div>
                </div>
              )}

              {}
              {submitSuccess ? (
                <div className="bg-white rounded-2xl shadow-sm border border-emerald-100 p-6 md:p-10 text-center">
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
                    <CheckCircle2 size={32} className="text-emerald-600 md:w-10 md:h-10" />
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-2">Pesanan Berhasil!</h2>
                  <p className="text-sm md:text-base text-slate-500 mb-6">Data pengiriman telah tersimpan permanen di sistem.</p>
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 md:p-6 max-w-md mx-auto mb-8">
                    <p className="text-xs md:text-sm text-slate-500 font-medium uppercase tracking-wider mb-2">Nomor Resi / AWB</p>
                    <p className="text-2xl md:text-3xl font-mono font-bold text-blue-700">{generatedResi}</p>
                  </div>
                  <div className="flex flex-col sm:flex-row justify-center gap-3 md:gap-4">
                    <button onClick={() => { const latestOrder = orders.find(o => o.id_order === generatedResi); if (latestOrder) handlePrintInvoice(latestOrder); }} className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 md:py-2.5 px-6 rounded-xl md:rounded-lg transition-colors flex items-center justify-center gap-2 shadow-md">
                      <Printer size={18} /> Cetak Invoice
                    </button>
                    <button onClick={resetForm} className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 md:py-2.5 px-6 rounded-xl md:rounded-lg transition-colors flex items-center justify-center gap-2 shadow-md">
                      <FilePlus2 size={18} /> Pesanan Baru
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleEntrySubmit} className="space-y-4 md:space-y-6 pb-8">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                    
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                      <div className="px-4 md:px-6 py-3 md:py-4 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
                        <Users size={18} className="text-blue-600" />
                        <h3 className="font-semibold text-slate-800 text-sm md:text-base">Informasi Pelanggan</h3>
                      </div>
                      <div className="p-4 md:p-6 space-y-5 md:space-y-6">
                        <div>
                          <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Data Pengirim</h4>
                          <div className="space-y-3 md:space-y-4">
                            
                            <div>
                              <label className="block text-xs md:text-sm font-medium text-slate-700 mb-1">
                                Nama Lengkap / Perusahaan *
                              </label>
                              <div className="flex gap-2">
                                <input 
                                  name="nama_pengirim" 
                                  value={formData.nama_pengirim} 
                                  onChange={handleInputChange} 
                                  required 
                                  type="text" 
                                  className="w-full p-3 md:p-2.5 bg-slate-50 border border-slate-200 rounded-xl md:rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm md:text-base text-slate-800" 
                                  placeholder="Contoh: Budi Santoso" 
                                />
                                <button
                                  type="button"
                                  onClick={() => setIsSelectCustomerModalOpen(true)}
                                  title="Pilih dari Data Pelanggan"
                                  className="p-2.5 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-xl md:rounded-lg border border-blue-200 transition-colors flex items-center justify-center shrink-0 cursor-pointer"
                                >
                                  <User size={18} />
                                </button>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                              <div>
                                <label className="block text-xs md:text-sm font-medium text-slate-700 mb-1">No. WhatsApp *</label>
                                <input name="no_wa_pengirim" value={formData.no_wa_pengirim} onChange={handleNumberChange} required type="tel" className="w-full p-3 md:p-2.5 bg-slate-50 border border-slate-200 rounded-xl md:rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm md:text-base" placeholder="0812..." />
                              </div>
                              <div>
                                <label className="block text-xs md:text-sm font-medium text-slate-700 mb-1">No. Identitas KTP *</label>
                                <input name="no_ktp_pengirim" value={formData.no_ktp_pengirim} onChange={handleNumberChange} required type="text" maxLength={16} className="w-full p-3 md:p-2.5 bg-slate-50 border border-slate-200 rounded-xl md:rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm md:text-base" placeholder="16 Digit NIK KTP" />
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs md:text-sm font-medium text-slate-700 mb-1">Alamat Pengirim</label>
                              <textarea name="alamat_pengirim" value={formData.alamat_pengirim} onChange={handleInputChange} rows={2} className="w-full p-3 md:p-2.5 bg-slate-50 border border-slate-200 rounded-xl md:rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm md:text-base resize-none" placeholder="Isi alamat lengkap pengirim..."></textarea>
                            </div>
                          </div>
                        </div>
                        <div className="border-t border-slate-100 my-1 md:my-2"></div>
                        <div>
                          <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Data Penerima</h4>
                          <div className="space-y-3 md:space-y-4">
                            <div>
                              <label className="block text-xs md:text-sm font-medium text-slate-700 mb-1">Nama Lengkap / Perusahaan *</label>
                              <input name="nama_penerima" value={formData.nama_penerima} onChange={handleInputChange} required type="text" className="w-full p-3 md:p-2.5 bg-slate-50 border border-slate-200 rounded-xl md:rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm md:text-base" placeholder="Contoh: Andi Wijaya" />
                            </div>
                            <div>
                              <label className="block text-xs md:text-sm font-medium text-slate-700 mb-1">No. WhatsApp *</label>
                              <input name="no_wa_penerima" value={formData.no_wa_penerima} onChange={handleNumberChange} required type="tel" className="w-full p-3 md:p-2.5 bg-slate-50 border border-slate-200 rounded-xl md:rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm md:text-base" placeholder="0812..." />
                            </div>
                            <div>
                              <label className="block text-xs md:text-sm font-medium text-slate-700 mb-1">Alamat (Door to Door)</label>
                              <textarea name="alamat_penerima" value={formData.alamat_penerima} onChange={handleInputChange} rows={2} className="w-full p-3 md:p-2.5 bg-slate-50 border border-slate-200 rounded-xl md:rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm md:text-base resize-none" placeholder="Isi alamat lengkap..."></textarea>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {}
                    <div className="space-y-4 md:space-y-6">
                      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                        <div className="px-4 md:px-6 py-3 md:py-4 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
                          <Car size={18} className="text-blue-600" />
                          <h3 className="font-semibold text-slate-800 text-sm md:text-base">Detail Kendaraan</h3>
                        </div>
                        <div className="p-4 md:p-6 space-y-3 md:space-y-4">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                            <div>
                              <label className="block text-xs md:text-sm font-medium text-slate-700 mb-1">Merk & Model *</label>
                              <input name="merk_tipe_kendaraan" value={formData.merk_tipe_kendaraan} onChange={handleInputChange} required type="text" className="w-full p-3 md:p-2.5 bg-slate-50 border border-slate-200 rounded-xl md:rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm md:text-base" placeholder="Cth: Toyota Fortuner" />
                            </div>
                            <div>
                              <label className="block text-xs md:text-sm font-medium text-slate-700 mb-1">Warna *</label>
                              <input name="warna_kendaraan" value={formData.warna_kendaraan} onChange={handleInputChange} required type="text" className="w-full p-3 md:p-2.5 bg-slate-50 border border-slate-200 rounded-xl md:rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm md:text-base" placeholder="Cth: Hitam" />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                            <div>
                              <label className="block text-xs md:text-sm font-medium text-slate-700 mb-1">Nomor Polisi / STNK *</label>
                              <input name="nomor_polisi" value={formData.nomor_polisi} onChange={handleInputChange} required type="text" className="w-full p-3 md:p-2.5 bg-slate-50 border border-slate-200 rounded-xl md:rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm md:text-base font-mono uppercase" placeholder="B 1234 ABC" />
                            </div>
                            <div>
                              <label className="block text-xs md:text-sm font-medium text-slate-700 mb-1">Tahun Pembuatan</label>
                              <input name="tahun_kendaraan" value={formData.tahun_kendaraan} onChange={handleNumberChange} type="text" maxLength={4} className="w-full p-3 md:p-2.5 bg-slate-50 border border-slate-200 rounded-xl md:rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm md:text-base" placeholder="2022" />
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs md:text-sm font-medium text-slate-700 mb-1">Golongan Kendaraan *</label>
                            <select 
                              name="golongan_kendaraan" 
                              value={formData.golongan_kendaraan} 
                              onChange={handleInputChange} 
                              required 
                              className={`w-full p-3 md:p-2.5 bg-slate-50 border border-slate-200 rounded-xl md:rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm md:text-base ${formData.golongan_kendaraan === '' ? 'text-slate-400' : 'text-slate-800'}`}
                            >
                              <option value="" disabled hidden>Pilih Golongan...</option>
                              {uniqueGolonganOptions.map((golName, idx) => (
                                <option key={idx} value={golName} className="text-slate-800">{golName}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs md:text-sm font-medium text-slate-700 mb-1">Catatan Fisik (Opsional)</label>
                            <input name="catatan_kendaraan" value={formData.catatan_kendaraan} onChange={handleInputChange} type="text" className="w-full p-3 md:p-2.5 bg-slate-50 border border-slate-200 rounded-xl md:rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm md:text-base" placeholder="Cth: Baret kecil di bumper depan" />
                          </div>
                        </div>
                      </div>

                      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                        <div className="px-4 md:px-6 py-3 md:py-4 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
                          <MapPin size={18} className="text-blue-600" />
                          <h3 className="font-semibold text-slate-800 text-sm md:text-base">Rute & Biaya</h3>
                        </div>
                        <div className="p-4 md:p-6 space-y-3 md:space-y-4">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                            <div>
                              <label className="block text-xs md:text-sm font-medium text-slate-700 mb-1">Pelabuhan Asal *</label>
                              <select name="origin" value={formData.origin} onChange={handleInputChange} required className={`w-full p-3 md:p-2.5 bg-slate-50 border border-slate-200 rounded-xl md:rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm md:text-base ${formData.origin === '' ? 'text-slate-400' : 'text-slate-800'}`}>
                                <option value="" disabled hidden>Pilih...</option>
                                {ports.map(p => <option key={`asal-${p}`} value={p} className="text-slate-800">{p}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs md:text-sm font-medium text-slate-700 mb-1">Pelabuhan Tujuan *</label>
                              <select name="destination" value={formData.destination} onChange={handleInputChange} required className={`w-full p-3 md:p-2.5 bg-slate-50 border border-slate-200 rounded-xl md:rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm md:text-base ${formData.destination === '' ? 'text-slate-400' : 'text-slate-800'}`}>
                                <option value="" disabled hidden>Pilih...</option>
                                {ports.map(p => <option key={`tujuan-${p}`} value={p} className="text-slate-800">{p}</option>)}
                              </select>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                            <div>
                              <label className="block text-xs md:text-sm font-medium text-slate-700 mb-1">Jadwal Kapal *</label>
                              <div className="relative">
                                <Calendar size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                                <input name="tanggal_muat" value={formData.tanggal_muat} onChange={handleInputChange} required type="date" className="w-full pl-10 p-3 md:p-2.5 bg-slate-50 border border-slate-200 rounded-xl md:rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm md:text-base" />
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs md:text-sm font-medium text-slate-700 mb-1">Total Biaya (Rp) *</label>
                              <input required type="text" readOnly value={formData.harga_dasar_snapshot ? Number(formData.harga_dasar_snapshot).toLocaleString('id-ID') : ''} className="w-full p-3 md:p-2.5 bg-blue-50 border border-blue-200 rounded-xl md:rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm md:text-base font-bold text-blue-900 cursor-not-allowed" placeholder="0" />
                            </div>
                          </div>

                          <div className="pt-2 border-t border-slate-100 mt-2">
                            <label className="block text-xs md:text-sm font-medium text-slate-700 mb-3">Layanan Tambahan (Opsional)</label>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                              <div>
                                <label className="block text-xs font-medium text-slate-500 mb-1">Asuransi Pengiriman</label>
                                <select 
                                  name="asuransi_id" 
                                  value={formData.asuransi_id} 
                                  onChange={handleInputChange} 
                                  className={`w-full p-3 md:p-2.5 bg-slate-50 border border-slate-200 rounded-xl md:rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm md:text-base ${formData.asuransi_id === '' ? 'text-slate-400' : 'text-slate-800'}`}
                                >
                                  <option value="">Tanpa Asuransi</option>
                                  {layananTambahanList.map((item) => (
                                    <option key={item.id_price_asuransi} value={item.id_price_asuransi} className="text-slate-800">
                                      {formatLabel(item.tipe_kendaraan)} (+Rp {Number(item.harga_asuransi).toLocaleString('id-ID')})
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-slate-500 mb-1">Door to Door</label>
                                <select 
                                  name="door_to_door_id" 
                                  value={formData.door_to_door_id} 
                                  onChange={handleInputChange} 
                                  className={`w-full p-3 md:p-2.5 bg-slate-50 border border-slate-200 rounded-xl md:rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm md:text-base ${formData.door_to_door_id === '' ? 'text-slate-400' : 'text-slate-800'}`}
                                >
                                  <option value="">Tanpa Door to Door</option>
                                  {layananDtdList.map((item) => (
                                    <option key={item.id_price_dtd} value={item.id_price_dtd} className="text-slate-800">
                                      {formatLabel(item.tipe_kendaraan)} (+Rp {Number(item.harga_dtd).toLocaleString('id-ID')})
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {}
                  <div className="fixed bottom-[4rem] md:bottom-auto left-0 right-0 md:static md:mt-6 bg-white p-4 md:p-6 shadow-[0_-4px_10px_rgba(0,0,0,0.05)] md:shadow-sm border-t md:border border-slate-200 md:rounded-2xl z-30 flex items-center justify-between gap-3">
                    <button type="button" onClick={resetForm} className="hidden sm:block px-6 py-3 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">Reset Form</button>
                    <button type="submit" disabled={isSubmitting} className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3.5 md:py-3 px-8 rounded-xl transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed text-base md:text-sm">
                      {isSubmitting ? 'Memproses...' : <>Simpan Pesanan <ArrowRight size={18} /></>}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {}
          {/* Dashboard Menu */}
          {activeMenu === 'dashboard' && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4 md:mb-6">
                <h1 className="text-xl md:text-2xl font-bold text-slate-900">Ringkasan Operasional</h1>
                <div className="w-full md:max-w-xs flex items-center bg-white rounded-full px-4 py-2 border border-slate-200 focus-within:ring-2 focus-within:ring-blue-500 transition-all shadow-sm">
                  <Search size={18} className="text-slate-400 shrink-0" />
                  <input type="text" placeholder="Cari resi, nama, status..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="bg-transparent border-none outline-none ml-2 w-full text-sm text-slate-700 placeholder-slate-400" />
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 md:mb-8">
                {stats.map((stat, idx) => (
                  <div key={idx} className="bg-white rounded-2xl p-5 shadow-sm border border-slate-200">
                    <p className="text-slate-500 text-sm font-medium mb-1">{stat.label}</p>
                    {stat.type === 'payment' ? (
                      <div className="flex items-center gap-5 mt-1">
                        <div className="flex flex-col">
                          <span className="text-2xl md:text-3xl font-bold text-emerald-600">{stat.lunas}</span>
                          <span className="text-[11px] font-semibold text-emerald-600/80 uppercase tracking-wider">Lunas</span>
                        </div>
                        <div className="h-8 w-px bg-slate-200"></div>
                        <div className="flex flex-col">
                          <span className="text-2xl md:text-3xl font-bold text-red-500">{stat.belum}</span>
                          <span className="text-[11px] font-semibold text-red-500/80 uppercase tracking-wider">Belum</span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-end gap-3">
                        <h3 className="text-2xl md:text-3xl font-bold text-slate-900">{stat.value}</h3>
                        <span className={`text-xs font-medium mb-1 ${stat.increase.startsWith('+') ? 'text-emerald-500' : 'text-slate-400'}`}>{stat.increase}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                  <h3 className="font-semibold text-slate-800 text-base md:text-lg">Pesanan Terbaru</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm whitespace-nowrap">
                    <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[11px] md:text-xs">
                      <tr>
                        <th className="px-5 py-3 font-semibold">Resi / AWB</th>
                        <th className="px-5 py-3 font-semibold">Pengirim</th>
                        <th className="px-5 py-3 font-semibold">Mobil</th>
                        <th className="px-5 py-3 font-semibold">Rute</th>
                        <th className="px-5 py-3 font-semibold">Tgl Entry</th>
                        <th className="px-5 py-3 font-semibold">Jadwal Kapal</th>
                        <th className="px-5 py-3 font-semibold">Status</th>
                        <th className="px-5 py-3 font-semibold text-center">Aksi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredOrders.length > 0 ? filteredOrders.map((order, idx) => (
                        <tr key={idx} className="hover:bg-slate-50 transition-colors">
                          <td className="px-5 py-4 font-mono font-medium text-slate-900">{order.id_order}</td>
                          <td className="px-5 py-4 text-slate-700">{order.nama_pengirim}</td>
                          <td className="px-5 py-4 text-slate-700">{order.merk_tipe_kendaraan}</td>
                          <td className="px-5 py-4 text-slate-700">{order.origin} - {order.destination}</td>
                          <td className="px-5 py-4 text-slate-700 whitespace-nowrap">{formatDate(order.created_at)}</td>
                          <td className="px-5 py-4 text-slate-700 whitespace-nowrap">{formatDate(order.tanggal_muat)}</td>
                          <td className="px-5 py-4">{renderStatusBadge(order.status_muatan)}</td>
                          <td className="px-5 py-4 text-center">
                            <button onClick={() => handlePrintInvoice(order)} title="Cetak Ulang Invoice" className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors inline-flex items-center gap-1.5 font-medium text-xs cursor-pointer"><Printer size={15} /> Cetak</button>
                          </td>
                        </tr>
                      )) : (
                        <tr><td colSpan={8} className="px-5 py-8 text-center text-slate-500">Pencarian "{searchQuery}" tidak ditemukan.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {}
          {/* Menu Pelacakan & Edit */}
          {activeMenu === 'tracking' && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4 md:mb-6">
                <div>
                  <h1 className="text-xl md:text-2xl font-bold text-slate-900">Update Pelacakan</h1>
                  <p className="text-slate-500 text-sm mt-1">Perbarui status lokasi pesanan secara real-time.</p>
                </div>
                <div className="w-full md:max-w-xs flex items-center bg-white rounded-full px-4 py-2 border border-slate-200/80 focus-within:ring-2 focus-within:ring-blue-500 transition-all shadow-sm">
                  <Search size={18} className="text-slate-400 shrink-0" />
                  <input type="text" placeholder="Cari resi, nama, status..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="bg-transparent border-none outline-none ml-2 w-full text-sm text-slate-700 placeholder-slate-400" />
                </div>
              </div>
              
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-200 bg-slate-50">
                  <h3 className="font-semibold text-slate-800 text-base">Daftar Status Pengiriman Aktif</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm whitespace-nowrap">
                    <thead className="bg-white text-slate-500 uppercase tracking-wider text-[11px] md:text-xs border-b border-slate-100">
                      <tr>
                        <th className="px-5 py-3 font-semibold">Resi / AWB</th>
                        <th className="px-5 py-3 font-semibold">Pengirim</th>
                        <th className="px-5 py-3 font-semibold">Rute</th>
                        <th className="px-5 py-3 font-semibold">Tgl Entry</th>
                        <th className="px-5 py-3 font-semibold">Jadwal Kapal</th>
                        <th className="px-5 py-3 font-semibold">Status</th>
                        <th className="px-5 py-3 font-semibold text-center">Pembayaran</th>
                        <th className="px-5 py-3 font-semibold text-center">Aksi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredOrders.length > 0 ? filteredOrders.map((order, idx) => (
                        <tr key={idx} className="hover:bg-slate-50 transition-colors">
                          <td className="px-5 py-4 font-mono font-medium text-slate-900">{order.id_order}</td>
                          <td className="px-5 py-4 text-slate-700">{order.nama_pengirim}<span className="block text-xs text-slate-400 mt-0.5">{order.merk_tipe_kendaraan}</span></td>
                          <td className="px-5 py-4 text-slate-700">{order.origin} - {order.destination}</td>
                          <td className="px-5 py-4 text-slate-700 whitespace-nowrap">{formatDate(order.created_at)}</td>
                          <td className="px-5 py-4 text-slate-700 whitespace-nowrap"><span className="inline-flex items-center gap-1.5"><Calendar size={14} className="text-slate-400"/> {formatDate(order.tanggal_muat)}</span></td>
                          <td className="px-5 py-4">{renderStatusBadge(order.status_muatan)}</td>
                          <td className="px-5 py-4 text-center">{renderPaymentBadge(order.status_payment)}</td>
                          <td className="px-5 py-4 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <button onClick={() => openChecklistModal(order)} title="Cek Fisik Kendaraan" className="p-2 text-slate-400 hover:bg-slate-100 hover:text-blue-600 rounded-lg transition-colors cursor-pointer"><ClipboardCheck size={16} /></button>
                              <button onClick={() => handlePrintInvoice(order)} title="Cetak Invoice" className="p-2 text-slate-400 hover:bg-slate-100 hover:text-blue-600 rounded-lg transition-colors cursor-pointer"><Printer size={16} /></button>
                              <button onClick={() => openEditModal(order)} title="Edit Data" className="p-2 text-slate-400 hover:bg-slate-100 hover:text-blue-600 rounded-lg transition-colors cursor-pointer"><Edit size={16} /></button>
                              <button onClick={() => openDeleteModal(order)} title="Hapus Pesanan" className="p-2 text-slate-400 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors cursor-pointer"><Trash2 size={16} /></button>
                            </div>
                          </td>
                        </tr>
                      )) : (
                        <tr><td colSpan={8} className="px-5 py-8 text-center text-slate-500">Pencarian "{searchQuery}" tidak ditemukan.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {}
          {/* Menu Data Pelanggan */}
          {activeMenu === 'customers' && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4 md:mb-6">
                <div>
                  <h1 className="text-xl md:text-2xl font-bold text-slate-900">Database Pelanggan</h1>
                  <p className="text-slate-500 text-sm mt-1">Kelola data informasi detail pelanggan dan riwayat transaksi mereka.</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                  <div className="w-full md:max-w-xs flex items-center bg-white rounded-full px-4 py-2 border border-slate-200 focus-within:ring-2 focus-within:ring-blue-500 transition-all shadow-sm">
                    <Search size={18} className="text-slate-400 shrink-0" />
                    <input type="text" placeholder="Cari pelanggan, KTP, WA..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="bg-transparent border-none outline-none ml-2 w-full text-sm text-slate-700 placeholder-slate-400" />
                  </div>
                  {/* Tombol Tambah Pelanggan */}
                  <button 
                    onClick={openAddCustomerModal}
                    className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-5 rounded-full transition-colors flex items-center justify-center gap-2 shadow-sm text-sm cursor-pointer"
                  >
                    <Plus size={18} /> Tambah
                  </button>
                </div>
              </div>
              
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-200 bg-slate-50">
                  <h3 className="font-semibold text-slate-800 text-base">Daftar Pelanggan Terdaftar</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm whitespace-nowrap">
                    <thead className="bg-white text-slate-500 uppercase tracking-wider text-[11px] md:text-xs border-b border-slate-100">
                      <tr>
                        <th className="px-5 py-3 font-semibold">ID Pelanggan</th>
                        <th className="px-5 py-3 font-semibold">Nama Pelanggan</th>
                        <th className="px-5 py-3 font-semibold">No. KTP / Identitas</th>
                        <th className="px-5 py-3 font-semibold">No. WhatsApp</th>
                        <th className="px-5 py-3 font-semibold text-center">Total Kiriman</th>
                        <th className="px-5 py-3 font-semibold text-center">Aksi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredCustomers.length > 0 ? filteredCustomers.map((cust, idx) => (
                        <tr key={idx} className="hover:bg-slate-50 transition-colors">
                          <td className="px-5 py-4 font-mono font-medium text-slate-900 text-xs" title={cust.id_customer}>{cust.id_customer.length > 13 ? cust.id_customer.substring(0,8) + '...' : cust.id_customer}</td>
                          <td className="px-5 py-4 text-slate-900 font-semibold">{cust.nama_pelanggan}</td>
                          <td className="px-5 py-4 text-slate-700">{cust.no_ktp}</td>
                          <td className="px-5 py-4 text-slate-700">{cust.no_wa}</td>
                          <td className="px-5 py-4 text-center">
                            <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full font-bold text-xs inline-block">{cust.total_kiriman}x Kirim</span>
                          </td>
                          <td className="px-5 py-4 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <button onClick={() => openEditCustomerModal(cust)} title="Edit Pelanggan" className="p-2 text-slate-400 hover:bg-slate-100 hover:text-blue-600 rounded-lg transition-colors cursor-pointer"><Edit size={16} /></button>
                              <button onClick={() => openDeleteCustomerModal(cust)} title="Hapus Pelanggan" className="p-2 text-slate-400 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors cursor-pointer"><Trash2 size={16} /></button>
                            </div>
                          </td>
                        </tr>
                      )) : (
                        <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-500">Pencarian pelanggan "{searchQuery}" tidak ditemukan.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {}
          {/* Menu Master Biaya */}
          {activeMenu === 'routes' && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4 md:mb-6">
                <div>
                  <h1 className="text-xl md:text-2xl font-bold text-slate-900">Master Biaya</h1>
                  <p className="text-slate-500 text-sm mt-1">Kelola data tarif pengiriman berdasarkan rute dan asuransi.</p>
                </div>

                <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
                  <div className="w-full md:max-w-xs flex items-center bg-white rounded-full px-4 py-2 border border-slate-200 focus-within:ring-2 focus-within:ring-blue-500 transition-all shadow-sm">
                    <Search size={18} className="text-slate-400 shrink-0" />
                    <input 
                      type="text" 
                      placeholder={masterBiayaTab === 'rates' ? "Cari rute, golongan..." : masterBiayaTab === 'tambahan' ? "Cari asuransi, tipe..." : "Cari layanan dtd..."}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="bg-transparent border-none outline-none ml-2 w-full text-sm text-slate-700 placeholder-slate-400"
                    />
                  </div>
                  {masterBiayaTab === 'rates' ? (
                    <button 
                      onClick={openAddTarifModal}
                      className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-full transition-colors flex items-center justify-center gap-2 shadow-sm text-sm cursor-pointer"
                    >
                      <Plus size={18} /> Tambah
                    </button>
                  ) : masterBiayaTab === 'tambahan' ? (
                    <button 
                      onClick={openAddTambahanModal}
                      className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-full transition-colors flex items-center justify-center gap-2 shadow-sm text-sm cursor-pointer"
                    >
                      <Plus size={18} /> Tambah
                    </button>
                  ) : (
                    <button 
                      onClick={openAddDtdModal}
                      className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-full transition-colors flex items-center justify-center gap-2 shadow-sm text-sm cursor-pointer"
                    >
                      <Plus size={18} /> Tambah
                    </button>
                  )}
                </div>
              </div>

              {/* Tab menu */}
              <div className="flex border-b border-slate-200 mb-6 bg-white rounded-t-2xl px-4 pt-2 border-x border-t overflow-x-auto hide-scrollbar">
                <button
                  onClick={() => { setMasterBiayaTab('rates'); setSearchQuery(''); }}
                  className={`py-3.5 px-6 font-bold text-sm border-b-2 transition-all cursor-pointer whitespace-nowrap ${
                    masterBiayaTab === 'rates'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  Tarif & Rute
                </button>
                <button
                  onClick={() => { setMasterBiayaTab('tambahan'); setSearchQuery(''); }}
                  className={`py-3.5 px-6 font-bold text-sm border-b-2 transition-all cursor-pointer whitespace-nowrap ${
                    masterBiayaTab === 'tambahan'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  Asuransi
                </button>
                <button
                  onClick={() => { setMasterBiayaTab('dtd'); setSearchQuery(''); }}
                  className={`py-3.5 px-6 font-bold text-sm border-b-2 transition-all cursor-pointer whitespace-nowrap ${
                    masterBiayaTab === 'dtd'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  Door to Door
                </button>
              </div>
              
              {}
              <div className="bg-white rounded-b-2xl shadow-sm border border-slate-200 overflow-hidden border-t-0">
                <div className="px-5 py-4 border-b border-slate-200 bg-slate-50">
                  <h3 className="font-semibold text-slate-800 text-base">
                    {masterBiayaTab === 'rates' ? 'Daftar Tarif Aktif (Price Rates)' : masterBiayaTab === 'tambahan' ? 'Daftar Asuransi (Price Asuransi)' : 'Daftar Door to Door (Price DTD)'}
                  </h3>
                </div>
                
                {masterBiayaTab === 'rates' ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm whitespace-nowrap">
                      <thead className="bg-white text-slate-500 uppercase tracking-wider text-[11px] md:text-xs border-b border-slate-100">
                        <tr>
                          <th className="px-5 py-3 font-semibold">ID</th>
                          <th className="px-5 py-3 font-semibold">Pelabuhan Asal</th>
                          <th className="px-5 py-3 font-semibold">Pelabuhan Tujuan</th>
                          <th className="px-5 py-3 font-semibold">Golongan Kendaraan</th>
                          <th className="px-5 py-3 font-semibold text-right">Harga Dasar (Rp)</th>
                          <th className="px-5 py-3 font-semibold text-center">Aksi</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {filteredTarif.length > 0 ? (
                          filteredTarif.map((tarif, idx) => (
                            <tr key={idx} className="hover:bg-slate-50 transition-colors">
                              <td className="px-5 py-4 font-mono font-medium text-slate-900">{tarif.id}</td>
                              <td className="px-5 py-4 text-slate-700">{tarif.origin}</td>
                              <td className="px-5 py-4 text-slate-700">{tarif.destination}</td>
                              <td className="px-5 py-4 text-slate-700">{tarif.golongan}</td>
                              <td className="px-5 py-4 text-slate-900 font-bold text-right">
                                {Number(tarif.harga_dasar).toLocaleString('id-ID')}
                              </td>
                              <td className="px-5 py-4 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <button 
                                    onClick={() => openEditTarifModal(tarif)}
                                    title="Edit Tarif"
                                    className="p-2 text-slate-400 hover:bg-slate-100 hover:text-blue-600 rounded-lg transition-colors cursor-pointer"
                                  >
                                    <Edit size={16} />
                                  </button>
                                  <button 
                                    onClick={() => openDeleteTarifModal(tarif)}
                                    title="Hapus Tarif"
                                    className="p-2 text-slate-400 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors cursor-pointer"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={6} className="px-5 py-8 text-center text-slate-500">
                              Pencarian tarif "{searchQuery}" tidak ditemukan.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : masterBiayaTab === 'tambahan' ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm whitespace-nowrap">
                      <thead className="bg-white text-slate-500 uppercase tracking-wider text-[11px] md:text-xs border-b border-slate-100">
                        <tr>
                          <th className="px-5 py-3 font-semibold">ID</th>
                          <th className="px-5 py-3 font-semibold">Nama Layanan</th>
                          <th className="px-5 py-3 font-semibold">Tipe Kendaraan</th>
                          <th className="px-5 py-3 font-semibold text-right">Harga Layanan (Rp)</th>
                          <th className="px-5 py-3 font-semibold text-center">Aksi</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {filteredTambahan.length > 0 ? (
                          filteredTambahan.map((tambahan, idx) => (
                            <tr key={idx} className="hover:bg-slate-50 transition-colors">
                              <td className="px-5 py-4 font-mono font-medium text-slate-900">{tambahan.id_price_asuransi}</td>
                              <td className="px-5 py-4 text-slate-700">
                                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold uppercase ${
                                  tambahan.nama_layanan === 'asuransi' ? 'bg-indigo-50 text-indigo-700' : 'bg-orange-50 text-orange-700'
                                }`}>
                                  {formatLabel(tambahan.nama_layanan)}
                                </span>
                              </td>
                              <td className="px-5 py-4 text-slate-700 capitalize">{formatLabel(tambahan.tipe_kendaraan)}</td>
                              <td className="px-5 py-4 text-slate-900 font-bold text-right">
                                {Number(tambahan.harga_asuransi).toLocaleString('id-ID')}
                              </td>
                              <td className="px-5 py-4 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <button 
                                    onClick={() => openEditTambahanModal(tambahan)}
                                    title="Edit Layanan"
                                    className="p-2 text-slate-400 hover:bg-slate-100 hover:text-blue-600 rounded-lg transition-colors cursor-pointer"
                                  >
                                    <Edit size={16} />
                                  </button>
                                  <button 
                                    onClick={() => openDeleteTambahanModal(tambahan)}
                                    title="Hapus Layanan"
                                    className="p-2 text-slate-400 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors cursor-pointer"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={5} className="px-5 py-8 text-center text-slate-500">
                              Pencarian data asuransi "{searchQuery}" tidak ditemukan.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm whitespace-nowrap">
                      <thead className="bg-white text-slate-500 uppercase tracking-wider text-[11px] md:text-xs border-b border-slate-100">
                        <tr>
                          <th className="px-5 py-3 font-semibold">ID</th>
                          <th className="px-5 py-3 font-semibold">Nama Layanan</th>
                          <th className="px-5 py-3 font-semibold">Tipe Kendaraan</th>
                          <th className="px-5 py-3 font-semibold text-right">Harga Layanan (Rp)</th>
                          <th className="px-5 py-3 font-semibold text-center">Aksi</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {filteredDtd.length > 0 ? (
                          filteredDtd.map((dtd, idx) => (
                            <tr key={idx} className="hover:bg-slate-50 transition-colors">
                              <td className="px-5 py-4 font-mono font-medium text-slate-900">{dtd.id_price_dtd}</td>
                              <td className="px-5 py-4 text-slate-700">
                                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold uppercase bg-amber-50 text-amber-700`}>
                                  {formatLabel(dtd.nama_layanan)}
                                </span>
                              </td>
                              <td className="px-5 py-4 text-slate-700 capitalize">{formatLabel(dtd.tipe_kendaraan)}</td>
                              <td className="px-5 py-4 text-slate-900 font-bold text-right">
                                {Number(dtd.harga_dtd).toLocaleString('id-ID')}
                              </td>
                              <td className="px-5 py-4 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <button 
                                    onClick={() => openEditDtdModal(dtd)}
                                    title="Edit Layanan DTD"
                                    className="p-2 text-slate-400 hover:bg-slate-100 hover:text-blue-600 rounded-lg transition-colors cursor-pointer"
                                  >
                                    <Edit size={16} />
                                  </button>
                                  <button 
                                    onClick={() => openDeleteDtdModal(dtd)}
                                    title="Hapus Layanan DTD"
                                    className="p-2 text-slate-400 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors cursor-pointer"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={5} className="px-5 py-8 text-center text-slate-500">
                              Pencarian data Door to Door "{searchQuery}" tidak ditemukan.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {}
          {/* Menu Pengaturan Sistem */}
          {activeMenu === 'settings' && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-8">
              
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden max-w-4xl">
                <div className="px-5 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Database size={18} className="text-blue-600" />
                    <h3 className="font-semibold text-slate-800 text-base">Inisialisasi Database Supabase</h3>
                  </div>
                  <span className={`px-2 py-0.5 text-[10px] rounded font-bold border ${supabaseClient ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-amber-50 text-amber-600 border-amber-200'}`}>
                    {supabaseClient ? 'Online Sync Active' : 'Local Sandbox Mode'}
                  </span>
                </div>
                <div className="p-6 space-y-4">
                  <p className="text-sm text-slate-500 leading-relaxed">
                    Agar aplikasi portal logistik **AntarOto** dapat menyimpan data secara permanen, aman, dan dapat diakses bersama tim lain secara real-time, harap hubungkan ke database Supabase Cloud milik Anda.
                  </p>
                  
                  <div className="flex flex-wrap gap-4 pt-2">
                    <button onClick={() => setShowConfigModal(true)} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 px-5 rounded-xl transition-all text-xs inline-flex items-center gap-1.5 cursor-pointer shadow-sm">
                      <Database size={14} /> Atur Kredensial Supabase
                    </button>
                    {supabaseClient && (
                      <button onClick={handleClearConfig} className="bg-red-50 hover:bg-red-100 text-red-600 font-bold py-2.5 px-5 rounded-xl transition-all text-xs inline-flex items-center gap-1.5 cursor-pointer">
                        <Trash2 size={14} /> Putus Koneksi Database
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-slate-900 rounded-2xl shadow-lg border border-slate-800 overflow-hidden max-w-4xl">
                <div className="px-5 py-4 border-b border-slate-800 bg-slate-950 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles size={18} className="text-amber-400" />
                    <h3 className="font-bold text-white text-sm md:text-base">Panduan Integrasi Database & Keamanan SQL</h3>
                  </div>
                  <span className="text-[10px] bg-slate-800 text-slate-400 px-2 py-0.5 rounded font-mono">Run in SQL Editor</span>
                </div>
                <div className="p-5 md:p-6 space-y-4">
                  <p className="text-xs md:text-sm text-slate-300">
                    Jika aplikasi Anda gagal menyimpan pesanan baru atau fitur Dropdown Asuransi tidak tersimpan dengan benar, pastikan kebijakan keamanan <strong className="text-white">RLS</strong> diizinkan dan tabel <strong className="text-white">orders</strong> memiliki kolom tambahan yang sesuai. <br/>Jalankan skrip berikut di <strong>SQL Editor</strong> Supabase Anda:
                  </p>
                  <pre className="bg-slate-950 p-4 rounded-xl border border-slate-800 text-[10px] md:text-xs text-emerald-400 font-mono overflow-x-auto max-h-60 custom-scrollbar select-text">
{`-- 1. Buka Akses Penuh ke Tabel Orders
ALTER TABLE orders ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Allow all operations for orders" ON public.orders FOR ALL USING (true) WITH CHECK (true);

-- 2. Buka Akses Penuh ke Tabel Customers
ALTER TABLE customers ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Allow all operations for customers" ON public.customers FOR ALL USING (true) WITH CHECK (true);

-- 3. Buka Akses Penuh ke Tabel Price Rates
ALTER TABLE price_rates ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Allow all operations for price rates" ON public.price_rates FOR ALL USING (true) WITH CHECK (true);

-- 4. Buka Akses Penuh ke Tabel Price Asuransi
ALTER TABLE price_asuransi ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Allow all operations for price asuransi" ON public.price_asuransi FOR ALL USING (true) WITH CHECK (true);

-- 5. Tambah Kolom Baru di Tabel Orders untuk Relasi Dropdown Layanan Tambahan & Tracking
ALTER TABLE orders ADD COLUMN IF NOT EXISTS asuransi_id INT;
ALTER TABLE orders ADD COLUMN IF NOT EXISTS door_to_door_id INT;
ALTER TABLE orders ADD COLUMN IF NOT EXISTS harga_asuransi NUMERIC DEFAULT 0;
ALTER TABLE orders ADD COLUMN IF NOT EXISTS harga_door_to_door NUMERIC DEFAULT 0;
ALTER TABLE orders ADD COLUMN IF NOT EXISTS tracking_updated_at TIMESTAMP WITH TIME ZONE;

-- 6. Buka Akses Penuh ke Tabel Price DTD
ALTER TABLE price_dtd ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Allow all operations for price dtd" ON public.price_dtd FOR ALL USING (true) WITH CHECK (true);

-- 7. Tambah Kolom JSONB untuk fitur Vehicle Checklist
ALTER TABLE orders ADD COLUMN IF NOT EXISTS checklist_data JSONB;

-- 8. Buat tabel tracking_history
CREATE TABLE IF NOT EXISTS tracking_history (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  id_order TEXT NOT NULL,
  status TEXT,
  catatan TEXT,
  photo_urls TEXT[],
  created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW()
);
ALTER TABLE tracking_history ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Allow all tracking history operations" ON public.tracking_history FOR ALL USING (true) WITH CHECK (true);

-- 9. Buat kebijakan Storage untuk bucket "tracking-photos"
-- (Pastikan bucket "tracking-photos" sudah dibuat secara manual dan diset PUBLIC di menu Storage)
CREATE POLICY "Allow public uploads tracking photos" ON storage.objects FOR INSERT WITH CHECK (bucket_id = 'tracking-photos');
CREATE POLICY "Allow public read tracking photos" ON storage.objects FOR SELECT USING (bucket_id = 'tracking-photos');
CREATE POLICY "Allow public update tracking photos" ON storage.objects FOR UPDATE USING (bucket_id = 'tracking-photos');
CREATE POLICY "Allow public delete tracking photos" ON storage.objects FOR DELETE USING (bucket_id = 'tracking-photos');
`}
                  </pre>
                </div>
              </div>

            </div>
          )}

        </div>
      </main>

      {}
      {/* ================= MODALS ================= */}
      {isAddCustomerModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden flex flex-col max-h-[90vh]">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50 shrink-0">
              <h3 className="font-bold text-lg text-slate-800">Tambah Pelanggan Baru</h3>
              <button onClick={() => { setIsAddCustomerModalOpen(false); setCustomerFormData({ nama_pelanggan: '', no_wa: '', no_ktp: '' }); }} className="text-slate-400 hover:text-red-500 p-1 cursor-pointer"><X size={20} /></button>
            </div>
            <div className="p-6 overflow-y-auto custom-scrollbar flex-1 min-h-0 bg-slate-50/50">
              
              {customerFormError && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-xs font-semibold flex items-center gap-2 mb-4 animate-in slide-in-from-top-1">
                  <AlertCircle size={14} className="shrink-0" />
                  <span>{customerFormError}</span>
                </div>
              )}

              <form id="addCustomerForm" onSubmit={saveAddCustomer} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Nama Pelanggan *</label>
                  <input 
                    name="nama_pelanggan" 
                    value={customerFormData.nama_pelanggan} 
                    onChange={(e) => setCustomerFormData({ ...customerFormData, nama_pelanggan: e.target.value })} 
                    required 
                    type="text" 
                    placeholder="Masukkan nama lengkap..."
                    className="w-full p-2.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500" 
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">No. WhatsApp / Telepon *</label>
                  <input 
                    name="no_wa" 
                    value={customerFormData.no_wa} 
                    onChange={(e) => setCustomerFormData({ ...customerFormData, no_wa: e.target.value.replace(/[^0-9]/g, '') })} 
                    required 
                    type="tel" 
                    placeholder="Contoh: 08123456789"
                    className="w-full p-2.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500" 
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">No. KTP / Identitas *</label>
                  <input 
                    name="no_ktp" 
                    value={customerFormData.no_ktp} 
                    onChange={(e) => setCustomerFormData({ ...customerFormData, no_ktp: e.target.value.replace(/[^0-9]/g, '') })} 
                    required 
                    type="text" 
                    maxLength={16}
                    placeholder="Masukkan 16 digit NIK..."
                    className="w-full p-2.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500" 
                  />
                </div>
              </form>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-3 shrink-0">
              <button type="button" onClick={() => { setIsAddCustomerModalOpen(false); setCustomerFormData({ nama_pelanggan: '', no_wa: '', no_ktp: '' }); }} className="px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">Batal</button>
              <button type="submit" form="addCustomerForm" disabled={isSubmitting} className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm shadow-md cursor-pointer disabled:opacity-50">
                <Save size={16} /> {isSubmitting ? 'Menyimpan...' : 'Simpan Pelanggan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {}
      {/* Modal Edit Pesanan Utama */}
      {isEditModalOpen && editingOrder && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50 shrink-0">
              <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
                Edit Data Lengkap <span className="text-blue-600 font-mono text-base bg-blue-100 px-2 py-0.5 rounded-md">{editingOrder.id_order}</span>
              </h3>
              <button onClick={() => { setIsEditModalOpen(false); setEditingOrder(null); }} className="text-slate-400 hover:text-red-500 p-1 cursor-pointer">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto custom-scrollbar flex-1 min-h-0 bg-slate-100">
              
              {editError && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-5 py-4 rounded-2xl text-sm font-semibold flex items-start gap-3 shadow-sm mb-6">
                  <AlertCircle size={20} className="shrink-0 mt-0.5" />
                  <div className="leading-relaxed">
                    <p className="font-bold mb-1">Gagal Mengubah Data di Supabase</p>
                    <p className="font-medium text-xs text-red-500/90">{editError}</p>
                  </div>
                </div>
              )}

              <form id="editForm" onSubmit={saveEditOrder} className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  
                  <div className="space-y-6">
                    
                    <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                      <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2"><UserCircle size={16}/> Data Pengirim</h4>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-1">Nama Pengirim *</label>
                          <input name="nama_pengirim" value={editingOrder.nama_pengirim || ''} onChange={handleEditChange} required type="text" className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-slate-700 mb-1">No. WhatsApp *</label>
                            <input name="no_wa_pengirim" value={editingOrder.no_wa_pengirim || ''} onChange={handleEditChange} required type="tel" className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-700 mb-1">No. KTP</label>
                            <input name="no_ktp_pengirim" value={editingOrder.no_ktp_pengirim || ''} onChange={handleEditChange} type="text" maxLength={16} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-1">Alamat Pengirim</label>
                          <textarea name="alamat_pengirim" value={editingOrder.alamat_pengirim || ''} onChange={handleEditChange} rows={2} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 resize-none"></textarea>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                      <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2"><UserCircle size={16}/> Data Penerima</h4>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-1">Nama Penerima *</label>
                          <input name="nama_penerima" value={editingOrder.nama_penerima || ''} onChange={handleEditChange} required type="text" className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-1">No. WhatsApp *</label>
                          <input name="no_wa_penerima" value={editingOrder.no_wa_penerima || ''} onChange={handleEditChange} required type="tel" className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-1">Alamat Penerima</label>
                          <textarea name="alamat_penerima" value={editingOrder.alamat_penerima || ''} onChange={handleEditChange} rows={2} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 resize-none"></textarea>
                        </div>
                      </div>
                    </div>

                    {}
                    <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                      <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2"><Car size={16}/> Detail Kendaraan</h4>
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-slate-700 mb-1">Merk & Model *</label>
                            <input name="merk_tipe_kendaraan" value={editingOrder.merk_tipe_kendaraan || ''} onChange={handleEditChange} required type="text" className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-700 mb-1">Warna *</label>
                            <input name="warna_kendaraan" value={editingOrder.warna_kendaraan || ''} onChange={handleEditChange} required type="text" className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500" />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-slate-700 mb-1">No. Polisi *</label>
                            <input name="nomor_polisi" value={editingOrder.nomor_polisi || ''} onChange={handleEditChange} required type="text" className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 uppercase font-mono" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-700 mb-1">Tahun</label>
                            <input name="tahun_kendaraan" value={editingOrder.tahun_kendaraan || ''} onChange={handleEditChange} type="text" maxLength={4} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-1">Golongan Kendaraan *</label>
                          <select 
                            name="golongan_kendaraan" 
                            value={editingOrder.golongan_kendaraan || ''} 
                            onChange={handleEditChange} 
                            required 
                            className={`w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 ${!editingOrder.golongan_kendaraan ? 'text-slate-400' : 'text-slate-800'}`}
                          >
                            <option value="" disabled hidden>Pilih Golongan...</option>
                            {uniqueGolonganOptions.map((golName, idx) => (
                              <option key={`edit-${idx}`} value={golName} className="text-slate-800">
                                {golName}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-1">Catatan Fisik (Opsional)</label>
                          <input name="catatan_kendaraan" value={editingOrder.catatan_kendaraan || ''} onChange={handleEditChange} type="text" className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                      </div>
                    </div>

                  </div>

                  <div className="space-y-6">
                    
                    {}
                    <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                      <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2"><MapPin size={16}/> Rute & Biaya</h4>
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-slate-700 mb-1">Pelabuhan Asal *</label>
                            <select name="origin" value={editingOrder.origin || ''} onChange={handleEditChange} required className={`w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 ${editingOrder.origin === '' ? 'text-slate-400' : 'text-slate-800'}`}>
                              <option value="" disabled hidden>Pilih...</option>
                              {ports.map(p => <option key={`e-asal-${p}`} value={p} className="text-slate-800">{p}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-700 mb-1">Pelabuhan Tujuan *</label>
                            <select name="destination" value={editingOrder.destination || ''} onChange={handleEditChange} required className={`w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 ${editingOrder.destination === '' ? 'text-slate-400' : 'text-slate-800'}`}>
                              <option value="" disabled hidden>Pilih...</option>
                              {ports.map(p => <option key={`e-tujuan-${p}`} value={p} className="text-slate-800">{p}</option>)}
                            </select>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-slate-700 mb-1">Jadwal Kapal *</label>
                            <input name="tanggal_muat" value={editingOrder.tanggal_muat || ''} onChange={handleEditChange} required type="date" className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-700 mb-1">Total Biaya (Rp) *</label>
                            <input readOnly type="text" value={editingOrder.harga_dasar_snapshot ? Number(editingOrder.harga_dasar_snapshot).toLocaleString('id-ID') : ''} className="w-full p-2.5 bg-blue-50 border border-blue-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 font-bold text-blue-900 cursor-not-allowed" />
                          </div>
                        </div>
                        
                        <div className="pt-2 border-t border-slate-100 mt-2">
                          <label className="block text-xs md:text-sm font-medium text-slate-700 mb-3">Layanan Tambahan (Opsional)</label>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                            <div>
                              <label className="block text-xs font-medium text-slate-500 mb-1">Asuransi Pengiriman</label>
                              <select 
                                name="asuransi_id" 
                                value={editingOrder.asuransi_id || ''} 
                                onChange={handleEditChange} 
                                className={`w-full p-3 md:p-2.5 bg-slate-50 border border-slate-200 rounded-xl md:rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm md:text-base ${!editingOrder.asuransi_id ? 'text-slate-400' : 'text-slate-800'}`}
                              >
                                <option value="">Tanpa Asuransi</option>
                                {layananTambahanList.map((item) => (
                                  <option key={`e-asuransi-${item.id_price_asuransi}`} value={item.id_price_asuransi} className="text-slate-800">
                                    {formatLabel(item.tipe_kendaraan)} (+Rp {Number(item.harga_asuransi).toLocaleString('id-ID')})
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-500 mb-1">Door to Door</label>
                              <select 
                                name="door_to_door_id" 
                                value={editingOrder.door_to_door_id || ''} 
                                onChange={handleEditChange} 
                                className={`w-full p-3 md:p-2.5 bg-slate-50 border border-slate-200 rounded-xl md:rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm md:text-base ${!editingOrder.door_to_door_id ? 'text-slate-400' : 'text-slate-800'}`}
                              >
                                <option value="">Tanpa Door to Door</option>
                                {layananDtdList.map((item) => (
                                  <option key={`e-dtd-${item.id_price_dtd}`} value={item.id_price_dtd} className="text-slate-800">
                                    {formatLabel(item.tipe_kendaraan)} (+Rp {Number(item.harga_dtd).toLocaleString('id-ID')})
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>

                      </div>
                    </div>

                    <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                      <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2"><Settings size={16}/> Update Operasional</h4>
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-slate-700 mb-1">Status Pengiriman</label>
                            <select name="status_muatan" value={editingOrder.status_muatan || 'Menunggu'} onChange={handleEditChange} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 font-medium text-slate-800 appearance-none">
                              <option value="Confirmed Booked">Confirmed Booked</option>
                              <option value="Proses Muat">Proses Muat</option>
                              <option value="Dalam Perjalanan">Dalam Perjalanan</option>
                              <option value="Tiba di Tujuan">Tiba di Tujuan</option>
                              <option value="Selesai">Selesai</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-700 mb-1">Tanggal & Waktu Status</label>
                            <input 
                              type="datetime-local" 
                              name="tanggal_update_status" 
                              value={editingOrder.tanggal_update_status || ''} 
                              onChange={handleEditChange} 
                              className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 font-medium text-slate-800" 
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-1">Catatan Pengiriman</label>
                          <textarea 
                            name="catatan_pengiriman" 
                            value={editingOrder.catatan_pengiriman || ''} 
                            onChange={handleEditChange} 
                            rows={2} 
                            className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 text-slate-800 resize-none" 
                            placeholder="Contoh: Menggunakan kapal Dharma Kencana VII / Cuaca buruk kapal terlambat..."
                          ></textarea>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-1">Status Pembayaran</label>
                          <select name="status_payment" value={editingOrder.status_payment || 'Belum'} onChange={handleEditChange} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 font-medium text-slate-800 appearance-none">
                            <option value="Belum">Belum Lunas</option>
                            <option value="Lunas">Lunas</option>
                          </select>
                        </div>

                        <div className="pt-4 mt-4 border-t border-slate-100">
                          <label className="block text-xs font-bold text-slate-700 mb-3 flex items-center gap-2">
                            <ImageIcon size={16} className="text-blue-500" /> Upload Dokumentasi Tracking
                          </label>
                          <TrackingPhotoUploader files={trackingPhotos} setFiles={setTrackingPhotos} />
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              </form>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 bg-white flex items-center justify-end gap-3 shrink-0">
              <button type="button" onClick={() => { setIsEditModalOpen(false); setEditingOrder(null); }} className="px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer">Batal</button>
              <button type="submit" form="editForm" disabled={isSubmitting} className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm shadow-md cursor-pointer disabled:opacity-70">
                {isUploadingPhotos ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                {isUploadingPhotos ? 'Mengunggah Foto...' : isSubmitting ? 'Menyimpan...' : 'Simpan Perubahan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {}
      {/* Modal Hapus Pesanan */}
      {isDeleteModalOpen && orderToDelete && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden flex flex-col">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle size={32} className="text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Hapus Data Pesanan?</h3>
              <p className="text-sm text-slate-500 mb-6">
                Apakah Anda yakin ingin membatalkan dan menghapus pesanan dengan Resi <span className="font-bold text-slate-700">{orderToDelete.id_order}</span>? Tindakan ini tidak dapat dikembalikan.
              </p>
              <div className="flex gap-3">
                <button onClick={() => { setIsDeleteModalOpen(false); setOrderToDelete(null); }} className="flex-1 px-4 py-2.5 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors cursor-pointer">Batal</button>
                <button onClick={confirmDeleteOrder} className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-colors shadow-md shadow-red-200 cursor-pointer">Ya, Hapus</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {}
      {/* Modal Checklist Kendaraan */}
      {isChecklistModalOpen && selectedChecklistOrder && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-6xl overflow-hidden flex flex-col h-[95vh]">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-blue-700 text-white shrink-0">
              <div className="flex items-center gap-3">
                <ClipboardCheck size={24} className="text-blue-200" />
                <div>
                  <h3 className="font-bold text-lg tracking-tight leading-tight">VEHICLE CHECKLIST</h3>
                  <p className="text-[11px] text-blue-200 font-medium">Bukti Serah Terima & Inspeksi Kondisi Kendaraan</p>
                </div>
              </div>
              <button onClick={() => setIsChecklistModalOpen(false)} className="text-blue-200 hover:text-white bg-blue-800/50 hover:bg-red-500 p-2 rounded-xl transition-colors cursor-pointer">
                <X size={20} />
              </button>
            </div>
            
            {/* Body */}
            <div className="overflow-y-auto flex-1 custom-scrollbar bg-slate-50 relative p-4 md:p-6 space-y-6">
              
              {/* Info Detail Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm col-span-2">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 border-b border-slate-100 pb-2">Informasi Umum</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <span className="block text-[10px] text-slate-500 font-semibold mb-0.5">Pengirim</span>
                      <strong className="text-sm text-slate-900">{selectedChecklistOrder.nama_pengirim}</strong>
                    </div>
                    <div>
                      <span className="block text-[10px] text-slate-500 font-semibold mb-0.5">Penerima</span>
                      <strong className="text-sm text-slate-900">{selectedChecklistOrder.nama_penerima}</strong>
                    </div>
                    <div>
                      <span className="block text-[10px] text-slate-500 font-semibold mb-0.5">Merk / Type</span>
                      <strong className="text-sm text-slate-900">{selectedChecklistOrder.merk_tipe_kendaraan}</strong>
                    </div>
                    <div>
                      <span className="block text-[10px] text-slate-500 font-semibold mb-0.5">Warna & No.Pol</span>
                      <strong className="text-sm text-slate-900 uppercase">{selectedChecklistOrder.warna_kendaraan} - {selectedChecklistOrder.nomor_polisi}</strong>
                    </div>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm space-y-3">
                  <div className="flex items-center gap-2">
                    <label className="text-[11px] font-bold text-slate-500 w-24">No. Rangka</label>
                    <input type="text" value={checklistFormData.no_rangka} onChange={(e)=>setChecklistFormData({...checklistFormData, no_rangka: e.target.value})} className="flex-1 bg-slate-50 border border-slate-200 rounded-lg p-1.5 text-xs outline-none focus:ring-1 focus:ring-blue-500 uppercase" placeholder="Masukkan..." />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-[11px] font-bold text-slate-500 w-24">No. Mesin</label>
                    <input type="text" value={checklistFormData.no_mesin} onChange={(e)=>setChecklistFormData({...checklistFormData, no_mesin: e.target.value})} className="flex-1 bg-slate-50 border border-slate-200 rounded-lg p-1.5 text-xs outline-none focus:ring-1 focus:ring-blue-500 uppercase" placeholder="Masukkan..." />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-[11px] font-bold text-slate-500 w-24">Speedometer</label>
                    <input type="text" value={checklistFormData.km_speedometer} onChange={(e)=>setChecklistFormData({...checklistFormData, km_speedometer: e.target.value})} className="flex-1 bg-slate-50 border border-slate-200 rounded-lg p-1.5 text-xs outline-none focus:ring-1 focus:ring-blue-500" placeholder="Km..." />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
                
                {/* Tabel Perlengkapan (Kiri - Lebar) */}
                <div className="xl:col-span-8 bg-white p-5 rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                  <h4 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <div className="w-2 h-6 bg-blue-600 rounded-full"></div> Informasi Perlengkapan Kendaraan
                  </h4>
                  <div className="overflow-x-auto flex-1 custom-scrollbar pb-2">
                    <table className="w-full text-left border-collapse min-w-[600px]">
                      <thead>
                        <tr className="bg-slate-100 text-slate-600 text-[10px] uppercase tracking-wider">
                          <th className="p-2 border border-slate-200 font-bold w-8 text-center">No</th>
                          <th className="p-2 border border-slate-200 font-bold w-48">Perlengkapan</th>
                          <th className="p-2 border border-slate-200 font-bold text-center w-12">Ada</th>
                          <th className="p-2 border border-slate-200 font-bold text-center w-12">Tdk</th>
                          <th className="p-2 border border-slate-200 font-bold text-center w-16">Jml</th>
                          <th className="p-2 border border-slate-200 font-bold">Status/Keterangan</th>
                        </tr>
                      </thead>
                      <tbody className="text-xs text-slate-700">
                        {CHECKLIST_ITEMS.reduce((result, item, index) => {
                          if (index % 2 === 0) result.push([CHECKLIST_ITEMS[index], CHECKLIST_ITEMS[index + 1]]);
                          return result;
                        }, []).map((pair, rowIndex) => (
                          <React.Fragment key={rowIndex}>
                            {pair.map((item, colIndex) => {
                              if (!item) return null;
                              const num = rowIndex * 2 + colIndex + 1;
                              const state = checklistFormData.items[item] || { ada: false, tidak_ada: false, jml: '', status: '' };
                              return (
                                <tr key={item} className="hover:bg-blue-50/50">
                                  <td className="p-1.5 border border-slate-200 text-center font-medium text-slate-500">{num}</td>
                                  <td className="p-1.5 border border-slate-200 font-semibold">{item}</td>
                                  <td className="p-1.5 border border-slate-200 text-center">
                                    <input type="checkbox" checked={state.ada} onChange={(e) => handleChecklistChange(item, 'ada', e.target.checked)} className="w-3.5 h-3.5 text-blue-600 rounded cursor-pointer" />
                                  </td>
                                  <td className="p-1.5 border border-slate-200 text-center">
                                    <input type="checkbox" checked={state.tidak_ada} onChange={(e) => handleChecklistChange(item, 'tidak_ada', e.target.checked)} className="w-3.5 h-3.5 text-red-600 rounded cursor-pointer" />
                                  </td>
                                  <td className="p-1.5 border border-slate-200">
                                    <input type="text" value={state.jml} onChange={(e) => handleChecklistChange(item, 'jml', e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded p-1 text-center outline-none focus:border-blue-500" />
                                  </td>
                                  <td className="p-1.5 border border-slate-200">
                                    <input type="text" value={state.status} onChange={(e) => handleChecklistChange(item, 'status', e.target.value)} placeholder="..." className="w-full bg-transparent border-none p-1 outline-none focus:ring-1 focus:ring-blue-500 rounded" />
                                  </td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {}
                {/* Diagram Mobil (Kanan - Sempit) */}
                <div className="xl:col-span-4 bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex flex-col items-center">
                  <h4 className="text-sm font-bold text-slate-800 w-full mb-2 flex items-center justify-between">
                    <span>Pemeriksaan Body</span>
                    <span className="text-[10px] font-normal bg-red-100 text-red-600 px-2 py-0.5 rounded-full">Klik area rusak</span>
                  </h4>
                  
                  {/* Interactive Top-Down Car Map */}
                  <div className="relative w-full max-w-[220px] aspect-[1/2] my-4 select-none drop-shadow-xl">
                    <svg viewBox="0 0 200 420" className="w-full h-full">
                      {/* Bayangan */}
                      <rect x="20" y="20" width="160" height="380" rx="30" fill="rgba(0,0,0,0.1)" transform="translate(5, 5)" />
                      
                      {/* Body Dasar */}
                      <path d="M 30,70 L 170,70 Q 185,70 185,100 L 185,320 Q 185,350 170,350 L 30,350 Q 15,350 15,320 L 15,100 Q 15,70 30,70 Z" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="3" />
                      
                      {/* Bumper Depan */}
                      <path d="M 35,40 L 165,40 Q 175,40 175,55 L 175,70 L 25,70 L 25,55 Q 25,40 35,40 Z" fill={checklistFormData.parts_damaged.includes('bumper_depan') ? '#ef4444' : '#cbd5e1'} stroke="#94a3b8" strokeWidth="2" className="cursor-pointer hover:brightness-95 transition-all" onClick={() => togglePartDamage('bumper_depan')} />
                      <text x="100" y="58" fontSize="11" fontWeight="bold" fill="#475569" textAnchor="middle" pointerEvents="none">BUMPER DPN</text>

                      {/* Kap Depan */}
                      <path d="M 30,70 L 170,70 L 165,130 L 35,130 Z" fill={checklistFormData.parts_damaged.includes('kap_depan') ? '#ef4444' : '#f8fafc'} stroke="#cbd5e1" strokeWidth="2" className="cursor-pointer hover:brightness-95 transition-all" onClick={() => togglePartDamage('kap_depan')} />
                      <text x="100" y="105" fontSize="12" fontWeight="bold" fill="#64748b" textAnchor="middle" pointerEvents="none">KAP</text>

                      {/* Kaca Depan */}
                      <path d="M 35,130 L 165,130 L 155,160 L 45,160 Z" fill={checklistFormData.parts_damaged.includes('kaca_depan') ? '#ef4444' : '#bae6fd'} stroke="#7dd3fc" strokeWidth="2" className="cursor-pointer hover:brightness-95 transition-all" onClick={() => togglePartDamage('kaca_depan')} />
                      
                      {/* Atap */}
                      <rect x="45" y="160" width="110" height="90" fill={checklistFormData.parts_damaged.includes('atap') ? '#ef4444' : '#f8fafc'} stroke="#cbd5e1" strokeWidth="2" className="cursor-pointer hover:brightness-95 transition-all" onClick={() => togglePartDamage('atap')} />
                      <text x="100" y="210" fontSize="14" fontWeight="bold" fill="#64748b" textAnchor="middle" pointerEvents="none">ATAP</text>

                      {/* Kaca Belakang */}
                      <path d="M 45,250 L 155,250 L 165,280 L 35,280 Z" fill={checklistFormData.parts_damaged.includes('kaca_belakang') ? '#ef4444' : '#bae6fd'} stroke="#7dd3fc" strokeWidth="2" className="cursor-pointer hover:brightness-95 transition-all" onClick={() => togglePartDamage('kaca_belakang')} />

                      {/* Bagasi / Kap Blkg */}
                      <path d="M 35,280 L 165,280 L 170,350 L 30,350 Z" fill={checklistFormData.parts_damaged.includes('bagasi') ? '#ef4444' : '#f8fafc'} stroke="#cbd5e1" strokeWidth="2" className="cursor-pointer hover:brightness-95 transition-all" onClick={() => togglePartDamage('bagasi')} />
                      <text x="100" y="320" fontSize="12" fontWeight="bold" fill="#64748b" textAnchor="middle" pointerEvents="none">BAGASI</text>

                      {/* Bumper Belakang */}
                      <path d="M 25,350 L 175,350 L 175,365 Q 175,380 165,380 L 35,380 Q 25,380 25,365 Z" fill={checklistFormData.parts_damaged.includes('bumper_blkg') ? '#ef4444' : '#cbd5e1'} stroke="#94a3b8" strokeWidth="2" className="cursor-pointer hover:brightness-95 transition-all" onClick={() => togglePartDamage('bumper_blkg')} />
                      <text x="100" y="368" fontSize="11" fontWeight="bold" fill="#475569" textAnchor="middle" pointerEvents="none">BUMPER BLKG</text>

                      {/* Pintu Kiri Depan & Belakang */}
                      <path d="M 15,130 L 35,130 L 45,160 L 45,205 L 15,205 Z" fill={checklistFormData.parts_damaged.includes('pintu_kiri_dpn') ? '#ef4444' : '#f1f5f9'} stroke="#cbd5e1" strokeWidth="2" className="cursor-pointer hover:brightness-95 transition-all" onClick={() => togglePartDamage('pintu_kiri_dpn')} />
                      <path d="M 15,205 L 45,205 L 45,250 L 35,280 L 15,280 Z" fill={checklistFormData.parts_damaged.includes('pintu_kiri_blkg') ? '#ef4444' : '#f1f5f9'} stroke="#cbd5e1" strokeWidth="2" className="cursor-pointer hover:brightness-95 transition-all" onClick={() => togglePartDamage('pintu_kiri_blkg')} />
                      
                      {/* Pintu Kanan Depan & Belakang */}
                      <path d="M 185,130 L 165,130 L 155,160 L 155,205 L 185,205 Z" fill={checklistFormData.parts_damaged.includes('pintu_kanan_dpn') ? '#ef4444' : '#f1f5f9'} stroke="#cbd5e1" strokeWidth="2" className="cursor-pointer hover:brightness-95 transition-all" onClick={() => togglePartDamage('pintu_kanan_dpn')} />
                      <path d="M 185,205 L 155,205 L 155,250 L 165,280 L 185,280 Z" fill={checklistFormData.parts_damaged.includes('pintu_kanan_blkg') ? '#ef4444' : '#f1f5f9'} stroke="#cbd5e1" strokeWidth="2" className="cursor-pointer hover:brightness-95 transition-all" onClick={() => togglePartDamage('pintu_kanan_blkg')} />

                      {/* Roda Kiri Dpn/Blkg */}
                      <rect x="2" y="75" width="20" height="40" rx="5" fill="#334155" />
                      <rect x="2" y="295" width="20" height="40" rx="5" fill="#334155" />
                      {/* Roda Kanan Dpn/Blkg */}
                      <rect x="178" y="75" width="20" height="40" rx="5" fill="#334155" />
                      <rect x="178" y="295" width="20" height="40" rx="5" fill="#334155" />
                    </svg>
                  </div>

                  <div className="w-full">
                    <label className="block text-xs font-bold text-slate-700 mb-1">Catatan Tambahan Body / Lainnya</label>
                    <textarea 
                      value={checklistFormData.catatan_body}
                      onChange={(e) => setChecklistFormData({...checklistFormData, catatan_body: e.target.value})}
                      rows={3} 
                      className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      placeholder="Cth: Baret panjang di pintu kiri, bemper depan penyok sedikit..."
                    ></textarea>
                  </div>
                </div>

              </div>

            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-200 bg-white flex items-center justify-between shrink-0">
              <div className="text-[10px] text-slate-500 max-w-xl hidden md:block">
                * Kondisi kendaraan saat diterima dalam keadaan sesuai dengan hasil inspeksi. Segala kerusakan/kerugian karena Force Majeur bukan tanggung jawab perusahaan.
              </div>
              <div className="flex items-center gap-3 w-full md:w-auto justify-end">
                <button type="button" onClick={() => handlePrintChecklist(selectedChecklistOrder, checklistFormData)} className="px-5 py-2.5 text-sm font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-xl transition-colors cursor-pointer flex items-center gap-2">
                  <Printer size={16} /> Cetak Form PDF
                </button>
                <button type="button" onClick={() => setIsChecklistModalOpen(false)} className="px-5 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer">Batal</button>
                <button type="button" onClick={saveChecklist} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2.5 px-6 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm shadow-md cursor-pointer">
                  <Save size={16} /> Simpan Checklist
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

      {}
      {/* Modal Edit Pelanggan */}
      {isEditCustomerModalOpen && editingCustomer && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden flex flex-col max-h-[90vh]">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50 shrink-0">
              <h3 className="font-bold text-lg text-slate-800">Edit Data Pelanggan</h3>
              <button onClick={() => { setIsEditCustomerModalOpen(false); setEditingCustomer(null); }} className="text-slate-400 hover:text-red-500 p-1 cursor-pointer"><X size={20} /></button>
            </div>
            <div className="p-6 overflow-y-auto custom-scrollbar flex-1 min-h-0">
              {customerFormError && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-xs font-semibold flex items-center gap-2 mb-4 animate-in slide-in-from-top-1">
                  <AlertCircle size={14} className="shrink-0" />
                  <span>{customerFormError}</span>
                </div>
              )}
              <form id="editCustomerForm" onSubmit={saveEditCustomer} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Nama Pelanggan</label>
                  <input name="nama_pelanggan" value={editingCustomer.nama_pelanggan} onChange={handleEditCustomerChange} required type="text" className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">No. WhatsApp</label>
                  <input name="no_wa" value={editingCustomer.no_wa} onChange={handleEditCustomerChange} required type="tel" className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">No. KTP / Identitas</label>
                  <input name="no_ktp" value={editingCustomer.no_ktp} onChange={handleEditCustomerChange} required type="text" maxLength={16} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </form>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-3 shrink-0">
              <button type="button" onClick={() => { setIsEditCustomerModalOpen(false); setEditingCustomer(null); }} className="px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">Batal</button>
              <button type="submit" form="editCustomerForm" disabled={isSubmitting} className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm shadow-md cursor-pointer disabled:opacity-50">
                <Save size={16} /> {isSubmitting ? 'Menyimpan...' : 'Simpan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isDeleteCustomerModalOpen && customerToDelete && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden flex flex-col">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle size={32} className="text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Hapus Data Pelanggan?</h3>
              <p className="text-sm text-slate-500 mb-6">
                Apakah Anda yakin ingin menghapus pelanggan <span className="font-bold text-slate-700">{customerToDelete.nama_pelanggan}</span> dari database? Data riwayat kiriman pelanggan tidak akan terhapus.
              </p>
              <div className="flex gap-3">
                <button onClick={() => { setIsDeleteCustomerModalOpen(false); setCustomerToDelete(null); }} className="flex-1 px-4 py-2.5 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors cursor-pointer">Batal</button>
                <button onClick={confirmDeleteCustomer} className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-colors shadow-md shadow-red-200 cursor-pointer">Ya, Hapus</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {}
      {/* Modal Tambah/Edit Tarif */}
      {isTarifModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50 shrink-0">
              <h3 className="font-bold text-lg text-slate-800">
                {tarifFormData.id ? 'Edit Tarif' : 'Tambah Tarif Baru'}
              </h3>
              <button onClick={() => { setIsTarifModalOpen(false); setTarifFormData({ id: '', origin: '', destination: '', golongan: '', harga_dasar: '' }); }} className="text-slate-400 hover:text-red-500 p-1 cursor-pointer"><X size={20} /></button>
            </div>
            <div className="p-6 overflow-y-auto custom-scrollbar flex-1 min-h-0 bg-slate-50/50">
              
              {tarifFormError && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-xs font-semibold flex items-center gap-2 mb-4 animate-in slide-in-from-top-1">
                  <AlertCircle size={14} className="shrink-0" />
                  <span>{tarifFormError}</span>
                </div>
              )}

              <form id="tarifForm" onSubmit={saveTarif} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Pelabuhan Asal</label>
                    <input 
                      name="origin" 
                      value={tarifFormData.origin} 
                      onChange={handleTarifInputChange} 
                      required 
                      list="ports-list"
                      placeholder="Ketik atau pilih pelabuhan..."
                      className="w-full p-2.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Pelabuhan Tujuan</label>
                    <input 
                      name="destination" 
                      value={tarifFormData.destination} 
                      onChange={handleTarifInputChange} 
                      required 
                      list="ports-list"
                      placeholder="Ketik atau pilih pelabuhan..."
                      className="w-full p-2.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Golongan Kendaraan</label>
                  <input 
                    name="golongan" 
                    value={tarifFormData.golongan} 
                    onChange={handleTarifInputChange} 
                    required 
                    list="golongan-list"
                    placeholder="Pilih atau ketik golongan baru..."
                    className="w-full p-2.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Harga Dasar (Rp)</label>
                  <input 
                    name="harga_dasar" 
                    value={tarifFormData.harga_dasar} 
                    onChange={handleTarifPriceChange} 
                    required 
                    type="text" 
                    placeholder="Contoh: 3500000"
                    className="w-full p-2.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 font-medium" 
                  />
                  <p className="text-[10px] text-slate-500 mt-1">Harga ini akan otomatis dipilih saat admin membuat pesanan dengan rute dan golongan yang sesuai.</p>
                </div>
              </form>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-3 shrink-0">
              <button type="button" onClick={() => { setIsTarifModalOpen(false); setTarifFormData({ id: '', origin: '', destination: '', golongan: '', harga_dasar: '' }); }} className="px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">Batal</button>
              <button type="submit" form="tarifForm" disabled={isSubmitting} className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm shadow-md cursor-pointer disabled:opacity-50">
                <Save size={16} /> {isSubmitting ? 'Menyimpan...' : 'Simpan Tarif'}
              </button>
            </div>
          </div>
        </div>
      )}

      {}
      {isDeleteTarifModalOpen && tarifToDelete && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden flex flex-col">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle size={32} className="text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Hapus Data Tarif?</h3>
              <p className="text-sm text-slate-500 mb-6">
                Yakin ingin menghapus tarif untuk rute <span className="font-bold text-slate-700">{tarifToDelete.origin} - {tarifToDelete.destination}</span> ({tarifToDelete.golongan})?
              </p>
              <div className="flex gap-3">
                <button onClick={() => { setIsDeleteTarifModalOpen(false); setTarifToDelete(null); }} className="flex-1 px-4 py-2.5 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors cursor-pointer">Batal</button>
                <button onClick={confirmDeleteTarif} className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-colors shadow-md shadow-red-200 cursor-pointer">Ya, Hapus</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {}
      {/* Modal Tambah/Edit Asuransi */}
      {isTambahanModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50 shrink-0">
              <h3 className="font-bold text-lg text-slate-800">
                {tambahanFormData.id_price_asuransi ? 'Edit Data Asuransi' : 'Tambah Asuransi Baru'}
              </h3>
              <button onClick={() => { setIsTambahanModalOpen(false); setTambahanFormData({ id_price_asuransi: '', nama_layanan: '', tipe_kendaraan: '', harga_asuransi: '' }); }} className="text-slate-400 hover:text-red-500 p-1 cursor-pointer"><X size={20} /></button>
            </div>
            <div className="p-6 overflow-y-auto custom-scrollbar flex-1 min-h-0 bg-slate-50/50">
              
              {tambahanFormError && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-xs font-semibold flex items-center gap-2 mb-4 animate-in slide-in-from-top-1">
                  <AlertCircle size={14} className="shrink-0" />
                  <span>{tambahanFormError}</span>
                </div>
              )}

              <form id="tambahanForm" onSubmit={saveTambahan} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Nama Layanan</label>
                  <select 
                    name="nama_layanan" 
                    value={tambahanFormData.nama_layanan} 
                    onChange={handleTambahanInputChange} 
                    required 
                    className="w-full p-2.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                  >
                    <option value="asuransi">Asuransi</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Tipe Kendaraan / Kategori</label>
                  <select 
                    name="tipe_kendaraan" 
                    value={tambahanFormData.tipe_kendaraan} 
                    onChange={handleTambahanInputChange} 
                    required 
                    className="w-full p-2.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 font-medium capitalize"
                  >
                    <option value="motor">Motor</option>
                    <option value="mobil">Mobil</option>
                    <option value="truk_sedang">Truk Sedang</option>
                    <option value="truk_besar_tronton">Truk Besar/Tronton</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Harga Tambahan (Rp)</label>
                  <input 
                    name="harga_asuransi" 
                    value={tambahanFormData.harga_asuransi} 
                    onChange={handleTambahanPriceChange} 
                    required 
                    type="text" 
                    placeholder="Contoh: 150000"
                    className="w-full p-2.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 font-medium" 
                  />
                  <p className="text-[10px] text-slate-500 mt-1">Biaya ini akan diaplikasikan sebagai nilai opsional pada formulir pesanan kendaraan.</p>
                </div>
              </form>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-3 shrink-0">
              <button type="button" onClick={() => { setIsTambahanModalOpen(false); setTambahanFormData({ id_price_asuransi: '', nama_layanan: '', tipe_kendaraan: '', harga_asuransi: '' }); }} className="px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">Batal</button>
              <button type="submit" form="tambahanForm" disabled={isSubmitting} className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm shadow-md cursor-pointer disabled:opacity-50">
                <Save size={16} /> {isSubmitting ? 'Menyimpan...' : 'Simpan Asuransi'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isDeleteTambahanModalOpen && tambahanToDelete && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden flex flex-col">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle size={32} className="text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Hapus Data Asuransi?</h3>
              <p className="text-sm text-slate-500 mb-6">
                Yakin ingin menghapus data asuransi <span className="font-bold text-slate-700 uppercase">{formatLabel(tambahanToDelete.nama_layanan)}</span> ({formatLabel(tambahanToDelete.tipe_kendaraan)})?
              </p>
              <div className="flex gap-3">
                <button onClick={() => { setIsDeleteTambahanModalOpen(false); setTambahanToDelete(null); }} className="flex-1 px-4 py-2.5 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors cursor-pointer">Batal</button>
                <button onClick={confirmDeleteTambahan} className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-colors shadow-md shadow-red-200 cursor-pointer">Ya, Hapus</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {}
      {/* Modal Tambah/Edit DTD */}
      {isDtdModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50 shrink-0">
              <h3 className="font-bold text-lg text-slate-800">
                {dtdFormData.id_price_dtd ? 'Edit Data Door to Door' : 'Tambah Layanan DTD Baru'}
              </h3>
              <button onClick={() => { setIsDtdModalOpen(false); setDtdFormData({ id_price_dtd: '', nama_layanan: 'door_to_door', tipe_kendaraan: 'mobil', harga_dtd: '' }); }} className="text-slate-400 hover:text-red-500 p-1 cursor-pointer"><X size={20} /></button>
            </div>
            <div className="p-6 overflow-y-auto custom-scrollbar flex-1 min-h-0 bg-slate-50/50">
              
              {dtdFormError && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-xs font-semibold flex items-center gap-2 mb-4 animate-in slide-in-from-top-1">
                  <AlertCircle size={14} className="shrink-0" />
                  <span>{dtdFormError}</span>
                </div>
              )}

              <form id="dtdForm" onSubmit={saveDtd} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Nama Layanan</label>
                  <select 
                    name="nama_layanan" 
                    value={dtdFormData.nama_layanan} 
                    onChange={handleDtdInputChange} 
                    required 
                    className="w-full p-2.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                  >
                    <option value="door_to_door">Door to Door</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Tipe Kendaraan / Kategori</label>
                  <select 
                    name="tipe_kendaraan" 
                    value={dtdFormData.tipe_kendaraan} 
                    onChange={handleDtdInputChange} 
                    required 
                    className="w-full p-2.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 font-medium capitalize"
                  >
                    <option value="motor">Motor</option>
                    <option value="mobil">Mobil</option>
                    <option value="truk_sedang">Truk Sedang</option>
                    <option value="truk_besar_tronton">Truk Besar/Tronton</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Harga Door to Door (Rp)</label>
                  <input 
                    name="harga_dtd" 
                    value={dtdFormData.harga_dtd} 
                    onChange={handleDtdPriceChange} 
                    required 
                    type="text" 
                    placeholder="Contoh: 300000"
                    className="w-full p-2.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 font-medium" 
                  />
                  <p className="text-[10px] text-slate-500 mt-1">Biaya ini akan diaplikasikan sebagai nilai opsional pada formulir pesanan kendaraan.</p>
                </div>
              </form>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-3 shrink-0">
              <button type="button" onClick={() => { setIsDtdModalOpen(false); setDtdFormData({ id_price_dtd: '', nama_layanan: 'door_to_door', tipe_kendaraan: 'mobil', harga_dtd: '' }); }} className="px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">Batal</button>
              <button type="submit" form="dtdForm" disabled={isSubmitting} className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm shadow-md cursor-pointer disabled:opacity-50">
                <Save size={16} /> {isSubmitting ? 'Menyimpan...' : 'Simpan DTD'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isDeleteDtdModalOpen && dtdToDelete && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden flex flex-col">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle size={32} className="text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Hapus Data DTD?</h3>
              <p className="text-sm text-slate-500 mb-6">
                Yakin ingin menghapus data layanan DTD <span className="font-bold text-slate-700 uppercase">{formatLabel(dtdToDelete.nama_layanan)}</span> ({formatLabel(dtdToDelete.tipe_kendaraan)})?
              </p>
              <div className="flex gap-3">
                <button onClick={() => { setIsDeleteDtdModalOpen(false); setDtdToDelete(null); }} className="flex-1 px-4 py-2.5 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors cursor-pointer">Batal</button>
                <button onClick={confirmDeleteDtd} className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-colors shadow-md shadow-red-200 cursor-pointer">Ya, Hapus</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {}
      {/* Modal Setup Supabase */}
      {showConfigModal && (
        <SupabaseConfigModal 
          onSave={handleSaveConfig} 
          onClear={handleClearConfig} 
          onClose={() => setShowConfigModal(false)} 
          currentUrl={supabaseUrl} 
          currentKey={supabaseKey} 
        />
      )}

      {}
      {/* Modal Pilih Pelanggan (Searchable) */}
      {isSelectCustomerModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[160] p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl border border-slate-100 shadow-2xl max-w-xl w-full max-h-[80vh] flex flex-col overflow-hidden">
            
            <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <div className="flex items-center gap-3">
                <Users size={22} className="text-blue-600 shrink-0" />
                <div>
                  <h3 className="font-bold text-slate-900 text-lg tracking-tight">Cari Pelanggan</h3>
                </div>
              </div>
              <button 
                onClick={() => setIsSelectCustomerModalOpen(false)}
                className="text-slate-400 hover:text-red-500 p-1.5 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 bg-white border-b border-slate-100">
              <div className="relative flex items-center w-full">
                <Search size={18} className="absolute left-3.5 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Cari nama atau nomor telepon..."
                  value={searchCustomerQuery}
                  onChange={(e) => setSearchCustomerQuery(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-slate-50/80 border border-slate-200 rounded-2xl text-sm outline-none focus:ring-2 focus:ring-blue-500 text-slate-800 transition-all placeholder-slate-400"
                />
              </div>
            </div>

            <div className="overflow-y-auto flex-1 p-5 space-y-3 bg-slate-50/30 custom-scrollbar">
              {customers && customers.filter(c => 
                c.nama_pelanggan?.toLowerCase().includes(searchCustomerQuery.toLowerCase()) ||
                c.no_wa?.includes(searchCustomerQuery) ||
                c.no_ktp?.includes(searchCustomerQuery)
              ).length > 0 ? (
                customers
                  .filter(c => 
                    c.nama_pelanggan?.toLowerCase().includes(searchCustomerQuery.toLowerCase()) ||
                    c.no_wa?.includes(searchCustomerQuery) ||
                    c.no_ktp?.includes(searchCustomerQuery)
                  )
                  .map((customer) => {
                    const initialLetter = customer.nama_pelanggan ? customer.nama_pelanggan.charAt(0).toUpperCase() : '?';
                    return (
                      <div
                        key={customer.id_customer}
                        className="bg-white border border-slate-200/80 rounded-2xl p-4 flex items-center justify-between shadow-sm hover:shadow-md hover:border-blue-200 transition-all group"
                      >
                        <div className="flex items-center min-w-0 flex-1 pr-4">
                          <div className="w-12 h-12 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-lg shrink-0 mr-4 shadow-inner">
                            {initialLetter}
                          </div>
                          
                          <div className="min-w-0">
                            <h4 className="text-base md:text-lg font-bold text-slate-900 truncate leading-snug group-hover:text-blue-600 transition-colors">
                              {customer.nama_pelanggan}
                            </h4>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 mt-1">
                              <p className="text-sm font-semibold text-slate-600 flex items-center gap-1">
                                <span className="text-slate-400 font-normal">Telp:</span> {customer.no_wa || '-'}
                              </p>
                              {customer.no_ktp && customer.no_ktp !== '-' && (
                                <>
                                  <span className="hidden sm:inline text-slate-300">•</span>
                                  <p className="text-sm font-medium text-slate-500">
                                    <span className="text-slate-400 font-normal">KTP:</span> {customer.no_ktp}
                                  </p>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <button
                          type="button"
                          onClick={() => pilihPelangganLama(customer)}
                          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-5 rounded-xl text-sm transition-all shadow-sm hover:shadow active:scale-[0.98] shrink-0 cursor-pointer"
                        >
                          Pilih
                        </button>
                      </div>
                    );
                  })
              ) : (
                <div className="p-12 text-center text-slate-400">
                  <Users size={40} className="mx-auto text-slate-300 mb-3" />
                  <p className="text-sm font-medium">Pelanggan tidak ditemukan atau database kosong.</p>
                </div>
              )}
            </div>
            
          </div>
        </div>
      )}

      {}
      {/* Datalist untuk Rute & Golongan */}
      <datalist id="ports-list">
        {ports.map((p, idx) => <option key={`dl-${idx}`} value={p} />)}
      </datalist>
      <datalist id="golongan-list">
        {uniqueGolonganOptions.map((gol, idx) => <option key={`gl-${idx}`} value={gol} />)}
      </datalist>

    </div>
  );
};

// ==================== REUSABLE MODAL CONFIG COMPONENT ====================
const SupabaseConfigModal = ({ onSave, onClear, onClose, currentUrl, currentKey }) => {
  const [url, setUrl] = useState(currentUrl);
  const [key, setKey] = useState(currentKey);

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center bg-slate-900/70 backdrop-blur-md p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh] border border-slate-200">
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50">
          <div className="flex items-center gap-2">
            <Database className="text-blue-600" size={20} />
            <h3 className="font-black text-slate-900 text-lg tracking-tight">Hubungkan Supabase Database</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-red-500 p-1 cursor-pointer">
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={(e) => onSave(e, url, key)} className="p-6 space-y-4 overflow-y-auto custom-scrollbar">
          <p className="text-xs text-slate-500 leading-relaxed mb-2">
            Salin parameter koneksi ini langsung dari panel dashboard Supabase Anda di menu **Project Settings &gt; API**.
          </p>

          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
              Supabase Project URL
            </label>
            <input 
              type="url" 
              required
              placeholder="https://your-project-id.supabase.co"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="appearance-none block w-full px-4 py-3 border border-slate-200 rounded-xl bg-slate-50/50 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium text-slate-800"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
              Supabase Anon Public API Key
            </label>
            <textarea 
              rows={3}
              required
              placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
              value={key}
              onChange={(e) => setKey(e.target.value)}
              className="appearance-none block w-full px-4 py-3 border border-slate-200 rounded-xl bg-slate-50/50 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs font-mono text-slate-800 resize-none"
            />
          </div>

          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 flex gap-3 text-xs text-blue-800 leading-relaxed">
            <Sparkles size={18} className="shrink-0 text-blue-600 mt-0.5" />
            <div>
              <strong className="font-bold block mb-0.5">Mode Real-time Cloud Aktif</strong>
              Setelah disimpan, sistem akan otomatis melakukan otentikasi nyata via server, memuat data pesanan langsung dari tabel, dan menonaktifkan simulasi lokal.
            </div>
          </div>

          <div className="pt-4 flex flex-col sm:flex-row gap-2.5">
            {currentUrl && (
              <button 
                type="button" 
                onClick={onClear}
                className="w-full sm:flex-1 py-3 px-4 rounded-xl border border-red-200 bg-red-50 hover:bg-red-100 text-red-600 text-sm font-bold transition-all cursor-pointer"
              >
                Hapus & Disconnect
              </button>
            )}
            <button 
              type="submit"
              className="w-full sm:flex-1 py-3 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold transition-all shadow-md shadow-blue-100 cursor-pointer"
            >
              Simpan & Hubungkan
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default App;
